import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CurrencyDisplay } from '../components/DirhamSymbol';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, X, History, ExternalLink, Search, HandMetal, TrendingDown, AlertOctagon, FileDown, Download, Camera, Scan, Loader, CheckCircle2, Upload, Eye, FileText, Navigation, Wifi, WifiOff, CircleDot, Circle, RefreshCw, MoreVertical, Clock, CheckCircle, Package, Bike, Truck } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { formatDateDisplay } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import MotorcycleBulkUpload from '../components/MotorcycleBulkUpload';
import BulkRegistrationCardUpload from '../components/BulkRegistrationCardUpload';
import { downloadMotorcycleTemplate } from '../utils/bulkUploadUtils';
import { useTranslation } from 'react-i18next';
import FileUpload from '../components/FileUpload';
import MultiPhotoUpload from '../components/MultiPhotoUpload';
import { generateHandoverPDF, generateTakeoverPDF, generateAccidentPDF } from '../utils/handoverTakeoverPdfUtils';
import ReplacementMotorcycle from '../components/ReplacementMotorcycle';
import LeaseHistoryReport from '../components/LeaseHistoryReport';
import BikeTrackingDialog from '../components/BikeTrackingDialog';
import GoFleetDeviceManager from '../components/GoFleetDeviceManager';
import PlatePair from '../components/PlatePair';
import { usePermissions, userCan } from '../utils/permissionUtils';
import { ScrollableTableContainer } from '../components/ui/scrollable-table-container';
import useDeviceDetection from '../hooks/useDeviceDetection';
import Pagination from '../components/Pagination';
import { formatErrorMessage } from '../utils/formatError';
import BiometricCapture, { uploadBiometrics } from '../components/BiometricCapture';

const Motorcycles = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile, isTablet } = useDeviceDetection();
  
  // Permission checks - CRITICAL for security
  const permissions = usePermissions(user, 'motorcycles');
  const canCreate = permissions.canCreate;
  const canEdit = permissions.canEdit;
  const canDelete = permissions.canDelete;
  const canViewHistory = permissions.canViewSubPage('history');
  const canViewHandover = permissions.canViewSubPage('handover');
  const canCreateHandover = permissions.canCreateSubPage('handover');
  const canViewTakeover = permissions.canViewSubPage('takeover');
  const canCreateTakeover = permissions.canCreateSubPage('takeover');
  const canViewDocuments = permissions.canViewSubPage('documents');
  const canCreateDocuments = permissions.canCreateSubPage('documents');
  const canDeleteDocuments = permissions.canDeleteSubPage('documents');
  
  const [motorcycles, setMotorcycles] = useState([]);
  const [allMotorcycles, setAllMotorcycles] = useState([]); // Full list for dropdowns (handover/takeover/accident forms)
  const [filteredMotorcycles, setFilteredMotorcycles] = useState([]);
  const [lessees, setLessees] = useState([]);
  const [riders, setRiders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingMotorcycle, setTrackingMotorcycle] = useState(null);
  const [gofleetManagerOpen, setGofleetManagerOpen] = useState(false);
  const [gpsStatuses, setGpsStatuses] = useState({}); // GPS status cache: { motorcycleId: { status, loading } }
  const [gpsLoadingAll, setGpsLoadingAll] = useState(false);
  const [gpsAutoRefreshEnabled, setGpsAutoRefreshEnabled] = useState(true);
  const [gpsLastRefresh, setGpsLastRefresh] = useState(null);
  const gpsRefreshIntervalRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'leased', 'available', 'pending'
  const [pendingMotorcycles, setPendingMotorcycles] = useState([]); // Motorcycles pending approval
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 50;
  
  // Stats for summary cards (fetched separately, not affected by pagination)
  const [stats, setStats] = useState({
    total: 0,
    leased: 0,
    available: 0,
    pending: 0,
    maintenance: 0
  });
  
  // Lessee-wise Summary state (expandable card above tabs)
  const [lesseeSummaryExpanded, setLesseeSummaryExpanded] = useState(false);
  const [lesseeSummary, setLesseeSummary] = useState([]);
  const [lesseeSummaryLoading, setLesseeSummaryLoading] = useState(false);
  const [expandedLesseeKey, setExpandedLesseeKey] = useState(null);

  // GPS auto-refresh interval (30 seconds)
  const GPS_REFRESH_INTERVAL = 30000;
  
  // Server-side search
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Logs data
  const [handovers, setHandovers] = useState([]);
  const [takeovers, setTakeovers] = useState([]);
  const [accidents, setAccidents] = useState([]);
  
  // Search and filter for logs
  const [handoverSearch, setHandoverSearch] = useState('');
  const [takeoverSearch, setTakeoverSearch] = useState('');
  const [accidentSearch, setAccidentSearch] = useState('');
  
  // Sync motorcycle data from rider assignments (runs automatically on page load)
  const syncRiderAssignmentsRef = useRef(false);
  useEffect(() => {
    if (syncRiderAssignmentsRef.current) return;
    syncRiderAssignmentsRef.current = true;
    const autoSync = async () => {
      try {
        await axios.post(`${API}/motorcycles/sync-rider-assignments`, {}, {
          headers: getAuthHeader()
        });
      } catch (error) {
        // Silent - auto sync failures are not critical
        console.log('Auto sync skipped:', error?.response?.status);
      }
    };
    autoSync();
  }, []);
  
  // OCR Processing function with 30-second timeout
  const processRegistrationCardOCR = async (imageUrl, file) => {
    setOcrProcessing(true);
    setOcrCountdown(30); // Reset countdown
    const OCR_TIMEOUT = 30000; // 30 seconds timeout
    let timeoutId = null;
    let countdownInterval = null;
    
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      
      // Start countdown timer
      countdownInterval = setInterval(() => {
        setOcrCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Set timeout to abort after 30 seconds
      timeoutId = setTimeout(() => {
        controller.abort();
      }, OCR_TIMEOUT);
      
      // Call backend OCR API to extract data from the uploaded registration card
      const formData = new FormData();
      formData.append('file', file);
      
      const ocrResponse = await axios.post(
        `${API}/ocr/vision/vehicle-registration`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          },
          signal: controller.signal,
          timeout: OCR_TIMEOUT
        }
      );
      
      // Clear timeout and countdown on success
      clearTimeout(timeoutId);
      clearInterval(countdownInterval);
      
      if (!ocrResponse.data.success || !ocrResponse.data.data) {
        throw new Error('OCR processing failed');
      }
      
      const extractedData = ocrResponse.data.data;
      setOcrResults(extractedData);
      
      // Upload file to server after OCR processing
      let serverFileUrl = '';
      if (file) {
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('file', file);
          
          const uploadResponse = await axios.post(
            `${API}/upload`,
            uploadFormData,
            {
              headers: {
                ...getAuthHeader(),
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          serverFileUrl = uploadResponse.data.url;
          console.log(`Registration card uploaded successfully: ${serverFileUrl}`);
          
          // Set preview for uploaded document
          setRegistrationCardPreview(serverFileUrl);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          toast.error('File upload failed. Please try again.');
          // Continue with OCR data even if upload fails
        }
      }
      
      // Auto-fill form with extracted data
      if (extractedData) {
        const newIndividualAutoFilled = {};
        
        setFormData(prevData => {
          const updatedFormData = { ...prevData };
          
          if (extractedData.plate_number) {
            updatedFormData.plate_number = extractedData.plate_number;
            newIndividualAutoFilled.plate_number = true;
          }
          if (extractedData.chassis_number) {
            // Ensure chassis number is uppercase and exactly 17 characters
            const chassis = extractedData.chassis_number?.toUpperCase()?.trim();
            if (chassis && chassis.length === 17) {
              updatedFormData.chassis_number = chassis;
              newIndividualAutoFilled.chassis_number = true;
            }
          }
          if (extractedData.make) {
            updatedFormData.brand = extractedData.make;
            newIndividualAutoFilled.brand = true;
          }
          if (extractedData.model) {
            updatedFormData.model_name = extractedData.model || '';
            newIndividualAutoFilled.model_name = true;
          }
          if (extractedData.color) {
            updatedFormData.color = extractedData.color;
            newIndividualAutoFilled.color = true;
          }
          if (extractedData.year) {
            const year = parseInt(extractedData.year);
            updatedFormData.model_year = year || '';
            updatedFormData.year_of_manufacturing = year || '';
            newIndividualAutoFilled.model_year = true;
          }
          if (extractedData.plate_emirate) {
            updatedFormData.city = extractedData.plate_emirate;
            newIndividualAutoFilled.city = true;
          }
          if (extractedData.engine_number) {
            updatedFormData.engine_number = extractedData.engine_number;
            newIndividualAutoFilled.engine_number = true;
          }
          if (extractedData.registration_date) {
            updatedFormData.registration_date = extractedData.registration_date;
            newIndividualAutoFilled.registration_date = true;
          }
          if (extractedData.expiry_date) {
            updatedFormData.registration_expiry_date = extractedData.expiry_date;
            newIndividualAutoFilled.registration_expiry_date = true;
          }
          
          // Use server URL if available, fallback to temp URL
          updatedFormData.registration_card_url = serverFileUrl || imageUrl;
          return updatedFormData;
        });
        
        setIndividualAutoFilled(newIndividualAutoFilled);
        setAutoFilled(true);
        toast.success('✅ Registration card scanned successfully! Form fields have been auto-filled.');
      }
      
    } catch (error) {
      // Clear timeout and countdown
      if (timeoutId) clearTimeout(timeoutId);
      if (countdownInterval) clearInterval(countdownInterval);
      
      // Check if it's a timeout error
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.name === 'CanceledError') {
        console.error('OCR timed out after 30 seconds');
        toast.error('⏱️ OCR scanning timed out after 30 seconds. Please try uploading again.', {
          duration: 5000
        });
      } else {
        console.error('OCR processing failed:', error);
        toast.error('Failed to process registration card. Please try again or fill the form manually.');
      }
    } finally {
      setOcrProcessing(false);
      setOcrCountdown(30); // Reset countdown
    }
  };

  // Parse OCR text to extract relevant information
  const parseRegistrationCardText = (text) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    const extracted = {};
    
    lines.forEach(line => {
      // Extract plate number
      const plateMatch = line.match(/Traffic Plate No\.?\s*(\d+)\s*\/?\s*(\d+)/i);
      if (plateMatch) {
        extracted.plate_number = `DXB ${plateMatch[1]}-${plateMatch[2]}`;
      }
      
      // Extract chassis number
      const chassisMatch = line.match(/Chassis No\.?\s*([A-Z0-9]+)/i);
      if (chassisMatch) {
        extracted.chassis_number = chassisMatch[1];
      }
      
      // Extract model/vehicle type
      const modelMatch = line.match(/Veh\. Type\s*(.*)/i) || line.match(/Model\s*(\d{4})/i);
      if (modelMatch) {
        if (line.match(/Veh\. Type/i)) {
          extracted.model_name = modelMatch[1];
        } else {
          extracted.year_of_manufacturing = parseInt(modelMatch[1]);
        }
      }
      
      // Extract color
      const colorMatch = line.match(/Color\s*(.*)/i);
      if (colorMatch) {
        extracted.color = colorMatch[1];
      }
      
      // Extract insurance expiry
      const insExpiryMatch = line.match(/Ins\. Exp\.?\s*(\d{2}\/\d{2}\/\d{4})/i);
      if (insExpiryMatch) {
        const [day, month, year] = insExpiryMatch[1].split('/');
        extracted.insurance_expiry_date = `${year}-${month}-${day}`;
      }
      
      // Extract city from "Place of Issue"
      if (line.includes('دبي') || line.toLowerCase().includes('dubai')) {
        extracted.city = 'Dubai';
      }
      
      // Extract insurance provider (mock data since not clearly visible in OCR)
      const policyMatch = line.match(/Policy No\.?\s*([A-Z0-9\/]+)/i);
      if (policyMatch && policyMatch[1].startsWith('DB')) {
        extracted.insurance_provider = 'Dubai Insurance Company';
      }
    });
    
    return extracted;
  };

  // Clear individual auto-filled field
  const clearIndividualField = (fieldName, defaultValue = '') => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: defaultValue
    }));
    
    setIndividualAutoFilled(prev => ({
      ...prev,
      [fieldName]: false
    }));
    
    toast.info(`${fieldName.replace('_', ' ')} cleared. You can now enter manually.`);
  };
  const filteredHandovers = handovers.filter(h => 
    (h.bike_plate_number || h.motorcycle_plate)?.toLowerCase().includes(handoverSearch.toLowerCase()) ||
    (h.bike_golden_plate_number || '')?.toLowerCase().includes(handoverSearch.toLowerCase()) ||
    h.lessee_name?.toLowerCase().includes(handoverSearch.toLowerCase()) ||
    h.rider_name?.toLowerCase().includes(handoverSearch.toLowerCase()) ||
    h.technician_name?.toLowerCase().includes(handoverSearch.toLowerCase())
  ).sort((a, b) => {
    const dateA = `${a.handover_date || ''} ${a.handover_time || ''}`.trim();
    const dateB = `${b.handover_date || ''} ${b.handover_time || ''}`.trim();
    return dateB.localeCompare(dateA);
  });
  
  const filteredTakeovers = takeovers.filter(t => 
    (t.bike_plate_number || t.motorcycle_plate)?.toLowerCase().includes(takeoverSearch.toLowerCase()) ||
    (t.bike_golden_plate_number || '')?.toLowerCase().includes(takeoverSearch.toLowerCase()) ||
    t.lessee_name?.toLowerCase().includes(takeoverSearch.toLowerCase()) ||
    t.rider_name?.toLowerCase().includes(takeoverSearch.toLowerCase()) ||
    t.technician_name?.toLowerCase().includes(takeoverSearch.toLowerCase())
  ).sort((a, b) => {
    const dateA = `${a.takeover_date || ''} ${a.takeover_time || ''}`.trim();
    const dateB = `${b.takeover_date || ''} ${b.takeover_time || ''}`.trim();
    return dateB.localeCompare(dateA);
  });
  
  const filteredAccidents = accidents.filter(a => 
    (a.bike_plate_number || a.motorcycle_plate)?.toLowerCase().includes(accidentSearch.toLowerCase()) ||
    (a.bike_golden_plate_number || '')?.toLowerCase().includes(accidentSearch.toLowerCase()) ||
    a.rider_name?.toLowerCase().includes(accidentSearch.toLowerCase()) ||
    (a.location || a.location_name)?.toLowerCase().includes(accidentSearch.toLowerCase()) ||
    a.police_report_type?.toLowerCase().includes(accidentSearch.toLowerCase())
  ).sort((a, b) => {
    const dateA = `${a.accident_date || ''} ${a.accident_time || ''}`.trim();
    const dateB = `${b.accident_date || ''} ${b.accident_time || ''}`.trim();
    return dateB.localeCompare(dateA);
  });
  const [activeFilter, setActiveFilter] = useState(null);
  // searchQuery is now defined above with debounce
  const [handoverDialogOpen, setHandoverDialogOpen] = useState(false);
  const [takeoverDialogOpen, setTakeoverDialogOpen] = useState(false);
  // Guards to prevent duplicate submissions when the user double-clicks the
  // Record button (each submit involves a biometric upload + a POST + a PDF
  // generation, so it can easily take 5–10 seconds during which the button
  // looks idle without these flags).
  const [submittingHandover, setSubmittingHandover] = useState(false);
  const [submittingTakeover, setSubmittingTakeover] = useState(false);
  const [accidentDialogOpen, setAccidentDialogOpen] = useState(false);

  // Admin-only Edit / Delete state for Handover & Takeover logs
  const canLog = (kind, action) => userCan(user, 'motorcycles', action, kind);
  const [logEditOpen, setLogEditOpen] = useState(false);
  const [logDeleteOpen, setLogDeleteOpen] = useState(false);
  const [logTarget, setLogTarget] = useState(null); // { kind: 'handover'|'takeover', record }
  const [logEditForm, setLogEditForm] = useState({});
  const [logDeleteForm, setLogDeleteForm] = useState({});
  const [logSubmitting, setLogSubmitting] = useState(false);

  const openLogEdit = (kind, record) => {
    setLogTarget({ kind, record });
    if (kind === 'handover') {
      setLogEditForm({
        handover_date: record.handover_date || '',
        handover_time: record.handover_time || '',
        lessee_name: record.lessee_name || '',
        lessee_id: record.lessee_id || '',
        rider_name: record.rider_name || '',
        rider_id: record.rider_id || '',
        rider_emirates_id: record.rider_emirates_id || '',
        technician_name: record.technician_name || '',
        location: record.location || record.location_name || '',
        odometer_km: record.odometer_km ?? '',
        notes: record.notes || '',
      });
    } else {
      setLogEditForm({
        takeover_date: record.takeover_date || '',
        takeover_time: record.takeover_time || '',
        lessee_name: record.lessee_name || '',
        lessee_id: record.lessee_id || '',
        rider_name: record.rider_name || '',
        rider_id: record.rider_id || '',
        rider_emirates_id: record.rider_emirates_id || '',
        technician_name: record.technician_name || '',
        location: record.location || record.location_name || '',
        odometer_km: record.odometer_km ?? '',
        takeover_type: record.takeover_type || 'temporary',
        temporary_start_date: record.temporary_start_date || '',
        temporary_end_date: record.temporary_end_date || '',
        temporary_reason: record.temporary_reason || '',
        notes: record.notes || '',
      });
    }
    setLogEditOpen(true);
  };

  const openLogDelete = (kind, record) => {
    setLogTarget({ kind, record });
    // Prefill "current state" with sensible defaults from the record so the
    // admin only needs to tweak what's changed in reality.
    setLogDeleteForm({
      status: 'available',
      lease_status: 'available',
      lessee_name: kind === 'handover' ? '' : (record.lessee_name || ''),
      lessee_id: kind === 'handover' ? '' : (record.lessee_id || ''),
      rider_name: kind === 'handover' ? '' : (record.rider_name || ''),
      rider_id: kind === 'handover' ? '' : (record.rider_id || ''),
      technician_name: record.technician_name || '',
      location: record.location || record.location_name || '',
      odometer_km: record.odometer_km ?? '',
      notes: '',
    });
    setLogDeleteOpen(true);
  };

  const submitLogEdit = async () => {
    if (!logTarget) return;
    const { kind, record } = logTarget;
    setLogSubmitting(true);
    try {
      const url = `${API}/motorcycles/${kind === 'handover' ? 'handovers' : 'takeovers'}/${record.id}`;
      await axios.put(url, logEditForm, { headers: getAuthHeader() });
      toast.success(`${kind === 'handover' ? 'Handover' : 'Takeover'} updated`);
      setLogEditOpen(false);
      setLogTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to update ${kind}`);
    } finally {
      setLogSubmitting(false);
    }
  };

  const submitLogDelete = async () => {
    if (!logTarget) return;
    const { kind, record } = logTarget;
    setLogSubmitting(true);
    try {
      const url = `${API}/motorcycles/${kind === 'handover' ? 'handovers' : 'takeovers'}/${record.id}`;
      await axios.delete(url, {
        headers: getAuthHeader(),
        data: { current_state: logDeleteForm },
      });
      toast.success(`${kind === 'handover' ? 'Handover' : 'Takeover'} deleted; bike state updated`);
      setLogDeleteOpen(false);
      setLogTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${kind}`);
    } finally {
      setLogSubmitting(false);
    }
  };

  const [swapLocations, setSwapLocations] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState({
    lessee: 'all',
    riderName: 'all',
    plateNumber: 'all',
    model: 'all',
    version: 'all',
    city: 'all',
    status: 'all',
    leaseStatus: 'all'
  });
  const [formData, setFormData] = useState({
    plate_number: '',
    golden_plate_number: '',
    chassis_number: '',
    brand: '',
    model_name: '',
    model_version: '',
    color: '',
    model_year: new Date().getFullYear(),
    year_of_manufacturing: new Date().getFullYear(),
    doors: 0,
    seats: 1,
    origin: '',
    engine_number: '',
    status: 'available',
    lease_status: 'available',
    lessee_id: '',
    lessee_name: '',
    lessee_contact: '',
    monthly_lease_amount: 0,
    lease_start_date: '',
    registration_date: '',
    registration_expiry_date: '',
    insurance_type: '',
    insurance_provider: '',
    insurance_start_date: '',
    insurance_expiry_date: '',
    city: '',
    salik_tag_no: '',
    assigned_rider_id: '',
    assigned_rider_name: '',
    registration_card_url: ''
  });

  // Registration card preview state
  const [registrationCardPreview, setRegistrationCardPreview] = useState(null);
  const [showReuploadDialog, setShowReuploadDialog] = useState(false);

  // Bulk upload state
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadImages, setBulkUploadImages] = useState(null);
  const [bulkUploadProcessing, setBulkUploadProcessing] = useState(false);
  const [bulkUploadResults, setBulkUploadResults] = useState(null);

  // OCR-related state
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrCountdown, setOcrCountdown] = useState(30); // 30 second countdown
  const [ocrResults, setOcrResults] = useState(null);
  const [autoFilled, setAutoFilled] = useState(false);
  const [individualAutoFilled, setIndividualAutoFilled] = useState({
    plate_number: false,
    chassis_number: false,
    brand: false,
    model_name: false,
    color: false,
    model_year: false,
    insurance_type: false,
    insurance_provider: false,
    insurance_expiry_date: false,
    city: false,
    engine_number: false,
    origin: false,
    registration_date: false,
    registration_expiry_date: false
  });

  // Catalogue of damaged/defective parts for the takeover certificate.
  // Fetched once on mount from /api/repair-items.
  const [repairItems, setRepairItems] = useState([]);
  const [defectSearch, setDefectSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/repair-items`, { headers: getAuthHeader() });
        const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setRepairItems(items);
      } catch (_) {
        // Non-fatal: the takeover dialog just shows an empty picker.
      }
    })();
  }, []);

  const [handoverForm, setHandoverForm] = useState({
    lessee_name: '',
    bike_id: '',
    bike_plate_number: '',
    handover_date: new Date().toISOString().split('T')[0],
    handover_time: new Date().toTimeString().slice(0, 5),
    location: '',
    technician_name: user?.name || '',
    rider_id: '',
    rider_name: '',
    rider_emirates_id: '',
    // Extra rider identity captured on the certificate (auto-filled from
    // the rider record; technician verifies with the rider and edits if
    // wrong or missing).
    rider_passport_no: '',
    rider_driving_license_no: '',
    rider_company_mobile: '',
    rider_personal_mobile: '',
    // Re-uploaded identity photos for the certificate's page 2 (per user
    // preference — not reused from the Talent PMS profile).
    rider_emirates_id_front_url: '',
    rider_emirates_id_back_url: '',
    rider_driving_license_front_url: '',
    rider_driving_license_back_url: '',
    notes: '',
    odometer_km: '',
    handover_photos: [],
    // Asset Handover Form generation flag — user ticks the checkbox
    // below the Handover Photos block. Only applicable to in-house
    // lessees (Ezy Mobility Solutions / Green Ezy / etc.). Platform is
    // auto-populated from the selected rider so the Word template's
    // `{{platform}}` placeholder resolves cleanly.
    generate_asset_handover_form: false,
    asset_handover_platform: '',
  });

  const [takeoverForm, setTakeoverForm] = useState({
    lessee_name: '',
    bike_id: '',
    bike_plate_number: '',
    takeover_date: new Date().toISOString().split('T')[0],
    takeover_time: new Date().toTimeString().slice(0, 5),
    location: '',
    technician_name: user?.name || '',
    rider_name: '',
    rider_emirates_id: '',
    takeover_type: 'temporary',
    temporary_start_date: '',
    temporary_end_date: '',
    temporary_reason: '',
    // Defects picked from maintenance_repair_items catalogue
    defects: [],
    notes: '',
    odometer_km: '',
    takeover_photos: []
  });

  const [accidentForm, setAccidentForm] = useState({
    accident_date: new Date().toISOString().split('T')[0],
    accident_time: new Date().toTimeString().slice(0, 5),
    bike_id: '',
    bike_plate_number: '',
    rider_name: '',
    location: '',
    police_report_type: 'green',
    police_report_photos: [],
    bike_photos: [],
    description: '',
    odometer_km: '',
    // Insurance claim flag and maintenance estimate
    submit_insurance_claim: false,
    estimated_damage_cost: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Apply filters when URL params or motorcycles data changes
    const filter = searchParams.get('filter');
    setActiveFilter(filter);
    applyFilter(filter);
  }, [searchParams, motorcycles]);

  useEffect(() => {
    // When debounced search changes, fetch from server (server-side search)
    // applySearch() is only for local advanced filters now
  }, [searchQuery, motorcycles]);

  // Trigger search manually (button click or Enter key)
  const handleSearch = () => {
    setActiveSearch(searchQuery);
    setCurrentPage(1);
    fetchData(1, searchQuery);
  };

  // When search text is cleared (backspace/delete), auto-reset to original state
  useEffect(() => {
    if (searchQuery === '' && activeSearch !== '') {
      setActiveSearch('');
      setCurrentPage(1);
      fetchData(1, '');
    }
  }, [searchQuery]);

  // When search is cleared via X button, reset immediately
  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setCurrentPage(1);
    fetchData(1, '');
  };

  useEffect(() => {
    // Apply advanced filters when they change (local filtering for now)
    applySearch();
  }, [advancedFilters, pendingMotorcycles]);

  // When summary card filter changes, fetch from server
  useEffect(() => {
    if (statusFilter === 'pending') {
      setFilteredMotorcycles(pendingMotorcycles);
    }
  }, [statusFilter, pendingMotorcycles]);

  const applyFilter = (filter) => {
    if (!filter) {
      setFilteredMotorcycles(motorcycles);
      return;
    }

    let filtered = [];
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

    switch (filter) {
      case 'available':
        filtered = motorcycles.filter(bike => bike.lease_status === 'available');
        break;
      case 'leased':
        filtered = motorcycles.filter(bike => bike.lease_status === 'leased');
        break;
      case 'maintenance':
        filtered = motorcycles.filter(bike => bike.status === 'maintenance');
        break;
      case 'insurance-expiring':
        filtered = motorcycles.filter(bike => {
          if (!bike.insurance_expiry_date) return false;
          const expiryDate = new Date(bike.insurance_expiry_date);
          return expiryDate >= today && expiryDate <= thirtyDaysFromNow;
        });
        break;
      default:
        filtered = motorcycles;
    }
    setFilteredMotorcycles(filtered);
  };

  const applySearch = () => {
    let filtered = motorcycles;

    // NOTE: Search is handled server-side via fetchData(), not client-side

    // Apply advanced filters
    if (advancedFilters.lessee && advancedFilters.lessee !== 'all') {
      filtered = filtered.filter(bike => bike.lessee_name === advancedFilters.lessee);
    }
    if (advancedFilters.riderName && advancedFilters.riderName !== 'all') {
      filtered = filtered.filter(bike => bike.assigned_rider_name === advancedFilters.riderName);
    }
    if (advancedFilters.plateNumber && advancedFilters.plateNumber !== 'all') {
      filtered = filtered.filter(bike => bike.plate_number === advancedFilters.plateNumber);
    }
    if (advancedFilters.model && advancedFilters.model !== 'all') {
      filtered = filtered.filter(bike => bike.model_name === advancedFilters.model);
    }
    if (advancedFilters.version && advancedFilters.version !== 'all') {
      filtered = filtered.filter(bike => bike.model_version === advancedFilters.version);
    }
    if (advancedFilters.city && advancedFilters.city !== 'all') {
      filtered = filtered.filter(bike => bike.city === advancedFilters.city);
    }
    if (advancedFilters.status && advancedFilters.status !== 'all') {
      filtered = filtered.filter(bike => bike.status === advancedFilters.status);
    }
    if (advancedFilters.leaseStatus && advancedFilters.leaseStatus !== 'all') {
      filtered = filtered.filter(bike => bike.lease_status === advancedFilters.leaseStatus);
    }

    // Status filter from summary cards is now handled server-side (passed to fetchData)
    // Only handle pending filter client-side (uses separate pendingMotorcycles list)
    if (statusFilter === 'pending') {
      filtered = pendingMotorcycles;
    }

    setFilteredMotorcycles(filtered);
  };

  const clearFilter = () => {
    setSearchParams({});
    setActiveFilter(null);
    setSearchQuery('');
    setActiveSearch('');
    setStatusFilter('all');
    setAdvancedFilters({
      lessee: 'all',
      riderName: 'all',
      plateNumber: 'all',
      model: 'all',
      version: 'all',
      city: 'all',
      status: 'all',
      leaseStatus: 'all'
    });
    setCurrentPage(1);
    fetchData(1, '', 'all');
  };

  const fetchData = async (page = currentPage, search = activeSearch, leaseStatus = null) => {
    try {
      setLoading(true);
      if (search) setIsSearching(true);
      const skip = (page - 1) * ITEMS_PER_PAGE;
      
      // Build query params
      const params = new URLSearchParams();
      params.append('limit', ITEMS_PER_PAGE);
      params.append('skip', skip);
      if (search) params.append('search', search);
      if (leaseStatus && leaseStatus !== 'all' && leaseStatus !== 'pending') {
        params.append('lease_status', leaseStatus);
      }
      
      // Build count params (same filters)
      const countParams = new URLSearchParams();
      if (search) countParams.append('search', search);
      if (leaseStatus && leaseStatus !== 'all' && leaseStatus !== 'pending') {
        countParams.append('lease_status', leaseStatus);
      }
      
      // Fetch paginated motorcycles, count, stats, and other data in parallel
      const [bikesRes, countRes, statsRes, lesseesRes, ridersRes, locationsRes, handoversRes, takeoversRes, accidentsRes, pendingRes, allBikesRes] = await Promise.all([
        axios.get(`${API}/motorcycles?${params.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/count?${countParams.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/lessees`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/all-realms/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/swap-locations`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/handovers`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/takeovers`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/accidents`, { headers: getAuthHeader() }),
        axios.get(`${API}/approvals/pending`, { headers: getAuthHeader() }).catch(() => ({ data: { motorcycles: [] } })),
        axios.get(`${API}/motorcycles/dropdown`, { headers: getAuthHeader() })
      ]);
      setMotorcycles(bikesRes.data);
      setAllMotorcycles(allBikesRes.data);
      setFilteredMotorcycles(bikesRes.data);
      setTotalCount(countRes.data.count || 0);
      setStats(statsRes.data);
      setRiders(ridersRes.data);
      setLessees(lesseesRes.data);
      setSwapLocations(locationsRes.data.filter(l => l.is_active));
      setHandovers(handoversRes.data);
      setTakeovers(takeoversRes.data);
      setAccidents(accidentsRes.data);
      setPendingMotorcycles(pendingRes.data?.motorcycles || []);
      
      // Fetch users only for Admin/Super Admin role
      if (userCan(user, 'users', 'view')) {
        try {
          const usersRes = await axios.get(`${API}/users`, { headers: getAuthHeader() });
          setUsers(usersRes.data);
        } catch (error) {
          console.log('Users endpoint not accessible');
          setUsers([]);
        }
      } else {
        setUsers([]);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };
  
  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, activeSearch, statusFilter);
  };

  // Fetch GPS status for a single motorcycle
  const fetchGpsStatus = async (motorcycleId, deviceId) => {
    if (!deviceId) return null;
    
    setGpsStatuses(prev => ({
      ...prev,
      [motorcycleId]: { ...prev[motorcycleId], loading: true }
    }));
    
    try {
      const response = await axios.get(`${API}/gofleet/devices/${deviceId}/status`, {
        headers: getAuthHeader()
      });
      const data = response.data;
      const status = {
        status: data.status || 'Unknown',
        isRunning: data.is_running,
        isIdle: data.is_idle,
        isOffline: data.is_offline,
        speed: data.speed || 0,
        lastCommunication: data.last_communication,
        loading: false
      };
      setGpsStatuses(prev => ({
        ...prev,
        [motorcycleId]: status
      }));
      return status;
    } catch (error) {
      setGpsStatuses(prev => ({
        ...prev,
        [motorcycleId]: { status: 'Error', loading: false, error: true }
      }));
      return null;
    }
  };

  // Lessee-wise summary fetcher
  const fetchLesseeSummary = async () => {
    setLesseeSummaryLoading(true);
    try {
      const res = await axios.get(`${API}/motorcycles/lessee-summary`, { headers: getAuthHeader() });
      setLesseeSummary(res.data.summary || []);
    } catch (err) {
      console.error('Failed to load lessee summary', err);
      toast.error('Failed to load lessee-wise summary');
    } finally {
      setLesseeSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (lesseeSummaryExpanded && lesseeSummary.length === 0) {
      fetchLesseeSummary();
    }
  }, [lesseeSummaryExpanded]);



  // Fetch GPS status for all linked motorcycles
  const fetchAllGpsStatuses = async (silent = false) => {
    const linkedMotorcycles = motorcycles.filter(m => m.gofleet_device_id);
    if (linkedMotorcycles.length === 0) return;
    
    if (!silent) {
      setGpsLoadingAll(true);
    }
    
    try {
      await Promise.all(
        linkedMotorcycles.map(m => fetchGpsStatus(m.id, m.gofleet_device_id))
      );
      setGpsLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching GPS statuses:', error);
    } finally {
      setGpsLoadingAll(false);
    }
  };

  // GPS statuses are now fetched on-demand via "Track Bike" action menu
  // Removed auto-fetch on page load to improve performance
  // useEffect(() => {
  //   if (motorcycles.length > 0) {
  //     fetchAllGpsStatuses();
  //   }
  // }, [motorcycles]);

  // Auto-refresh GPS statuses disabled - use "Track Bike" from action menu instead
  // useEffect(() => {
  //   if (gpsAutoRefreshEnabled && motorcycles.length > 0) {
  //     if (gpsRefreshIntervalRef.current) {
  //       clearInterval(gpsRefreshIntervalRef.current);
  //     }
  //     gpsRefreshIntervalRef.current = setInterval(() => {
  //       fetchAllGpsStatuses(true);
  //     }, GPS_REFRESH_INTERVAL);
  //     return () => {
  //       if (gpsRefreshIntervalRef.current) {
  //         clearInterval(gpsRefreshIntervalRef.current);
  //       }
  //     };
  //   }
  //   return () => {
  //     if (gpsRefreshIntervalRef.current) {
  //       clearInterval(gpsRefreshIntervalRef.current);
  //     }
  //   };
  // }, [gpsAutoRefreshEnabled, motorcycles.length]);

  // GPS Status badge render function - kept for potential future use
  const renderGpsStatusBadge = (motorcycle) => {
    const hasDevice = !!motorcycle.gofleet_device_id;
    const statusData = gpsStatuses[motorcycle.id];
    
    if (!hasDevice) {
      return (
        <Badge variant="outline" className="text-xs text-gray-400 gap-1">
          <WifiOff className="w-3 h-3" />
          No GPS
        </Badge>
      );
    }
    
    if (!statusData || statusData.loading) {
      return (
        <Badge variant="outline" className="text-xs text-blue-500 gap-1">
          <Loader className="w-3 h-3 animate-spin" />
          Loading
        </Badge>
      );
    }
    
    if (statusData.error) {
      return (
        <Badge variant="outline" className="text-xs text-red-500 gap-1">
          <WifiOff className="w-3 h-3" />
          Error
        </Badge>
      );
    }
    
    if (statusData.isRunning) {
      return (
        <Badge className="text-xs bg-green-100 text-green-700 gap-1">
          <CircleDot className="w-3 h-3" />
          Running
        </Badge>
      );
    }
    
    if (statusData.isIdle) {
      return (
        <Badge className="text-xs bg-amber-100 text-amber-700 gap-1">
          <Circle className="w-3 h-3" />
          Idle
        </Badge>
      );
    }
    
    if (statusData.isOffline) {
      return (
        <Badge className="text-xs bg-gray-100 text-gray-600 gap-1">
          <WifiOff className="w-3 h-3" />
          Offline
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline" className="text-xs text-gray-500 gap-1">
        <Wifi className="w-3 h-3" />
        {statusData.status}
      </Badge>
    );
  };

  const handleLesseeSelect = (lesseeId) => {
    const lessee = lessees.find(l => l.id === lesseeId);
    if (lessee) {
      setFormData({
        ...formData,
        lessee_id: lesseeId,
        lessee_name: lessee.company_name,
        lessee_contact: lessee.contact_mobile
      });
    }
  };

  const handleLesseeChange = (lesseeId) => {
    const lessee = lessees.find(l => l.id === lesseeId);
    setFormData({
      ...formData,
      lessee_id: lesseeId,
      lessee_name: lessee ? lessee.company_name : '',
      lessee_contact: lessee ? lessee.contact_number : ''
    });
  };

  const handleRiderChange = (riderId) => {
    if (riderId === "unassigned") {
      setFormData({
        ...formData,
        assigned_rider_id: '',
        assigned_rider_name: ''
      });
    } else {
      const rider = riders.find(r => r.id === riderId);
      const updatedFormData = {
        ...formData,
        assigned_rider_id: riderId,
        assigned_rider_name: rider ? rider.name : ''
      };
      
      // Auto-populate lessee if lease status is "leased" and rider has a lessee
      if (formData.lease_status === 'leased' && rider && rider.lessee_id) {
        const lessee = lessees.find(l => l.id === rider.lessee_id);
        if (lessee) {
          updatedFormData.lessee_id = lessee.id;
          updatedFormData.lessee_name = lessee.company_name;
          updatedFormData.lessee_contact = lessee.contact_mobile || lessee.contact_number || '';
        }
      }
      
      setFormData(updatedFormData);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate plate number format (e.g., DXB 2-33654)
    const plateRegex = /^[A-Z]{3}\s\d{1,2}-\d{5}$/;
    if (!plateRegex.test(formData.plate_number)) {
      toast.error('Plate number must be in format: DXB 2-33654');
      return;
    }
    
    // Validate chassis number - must be exactly 17 alphanumeric characters (VIN standard)
    const chassisClean = formData.chassis_number?.trim().toUpperCase() || '';
    if (chassisClean.length !== 17) {
      toast.error(`Chassis number must be exactly 17 characters. Current: ${chassisClean.length} characters`);
      return;
    }
    
    // Validate Salik tag number format (must be exactly 8 digits)
    if (formData.salik_tag_no && formData.salik_tag_no.length !== 8) {
      toast.error('Salik tag number must be exactly 8 digits');
      return;
    }
    
    try {
      // Ensure chassis number is uppercase
      const submitData = {
        ...formData,
        chassis_number: chassisClean
      };
      
      if (editingId) {
        await axios.put(`${API}/motorcycles/${editingId}`, submitData, {
          headers: getAuthHeader()
        });
        toast.success('Motorcycle updated successfully');
      } else {
        await axios.post(`${API}/motorcycles`, submitData, {
          headers: getAuthHeader()
        });
        toast.success('Motorcycle added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (motorcycle) => {
    setEditingId(motorcycle.id);
    setFormData({
      plate_number: motorcycle.plate_number,
      golden_plate_number: motorcycle.golden_plate_number || '',
      chassis_number: motorcycle.chassis_number,
      brand: motorcycle.brand || '',
      model_name: motorcycle.model_name,
      model_version: motorcycle.model_version,
      color: motorcycle.color,
      model_year: motorcycle.model_year || motorcycle.year_of_manufacturing,
      year_of_manufacturing: motorcycle.year_of_manufacturing,
      doors: motorcycle.doors || 0,
      seats: motorcycle.seats || 1,
      origin: motorcycle.origin || '',
      engine_number: motorcycle.engine_number || '',
      status: motorcycle.status,
      lease_status: motorcycle.lease_status,
      lessee_id: motorcycle.lessee_id || '',
      lessee_name: motorcycle.lessee_name || '',
      lessee_contact: motorcycle.lessee_contact || '',
      monthly_lease_amount: motorcycle.monthly_lease_amount || 0,
      lease_start_date: motorcycle.lease_start_date || '',
      registration_date: motorcycle.registration_date || '',
      registration_expiry_date: motorcycle.registration_expiry_date || '',
      insurance_type: motorcycle.insurance_type || '',
      insurance_provider: motorcycle.insurance_provider || '',
      insurance_start_date: motorcycle.insurance_start_date || '',
      insurance_expiry_date: motorcycle.insurance_expiry_date || '',
      city: motorcycle.city || '',
      salik_tag_no: motorcycle.salik_tag_no || '',
      assigned_rider_id: motorcycle.assigned_rider_id || '',
      assigned_rider_name: motorcycle.assigned_rider_name || '',
      registration_card_url: motorcycle.registration_card_url || ''
    });
    // Set preview for existing registration card
    if (motorcycle.registration_card_url) {
      setRegistrationCardPreview(motorcycle.registration_card_url);
    } else {
      setRegistrationCardPreview(null);
    }
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this motorcycle?')) {
      try {
        await axios.delete(`${API}/motorcycles/${id}`, {
          headers: getAuthHeader()
        });
        toast.success('Motorcycle deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete motorcycle');
      }
    }
  };

  const handleHandoverSubmit = async (e) => {
    e.preventDefault();
    // Double-submit guard — exits immediately on subsequent clicks while the
    // previous submit is still in flight.
    if (submittingHandover) return;

    // Mandatory biometric check — signature required, thumb print optional.
    const bioState = handoverForm.biometrics || {};
    if (!bioState.has_signature && !bioState.signature_url) {
      toast.error('Rider digital signature is required before recording the handover.');
      return;
    }

    setSubmittingHandover(true);
    try {
      // Upload signature + thumb-print first; merge URLs into the payload.
      const bio = await uploadBiometrics(handoverForm.biometrics);
      const payload = {
        ...handoverForm,
        rider_signature_url: bio.signature_url || null,
        rider_fingerprint_url: bio.fingerprint_url || null,
        rider_fingerprint_template_url: bio.fingerprint_template_url || null,
        rider_fingerprint_quality: bio.fingerprint_quality || null,
        rider_fingerprint_device_serial: bio.fingerprint_device_serial || null,
        biometrics_captured_at: bio.biometrics_captured_at || null,
      };
      delete payload.biometrics;
      const response = await axios.post(`${API}/motorcycles/handover`, payload, {
        headers: getAuthHeader()
      });
      
      // Auto-download PDF immediately
      const handoverData = { ...payload, id: response.data?.id || `HO-${Date.now()}` };
      const motorcycle = allMotorcycles.find(m => m.id === handoverForm.bike_id);

      // Server-side PDF: matches the DOCX certificate template exactly and
      // is generated from the freshly-inserted row (so the certificate no.
      // matches what's saved). Falls back to client jsPDF if the API 404s.
      const downloadHandoverServer = async () => {
        const handoverId = response.data?.id || handoverData.id;
        const res = await axios.get(
          `${API}/motorcycles/handovers/${handoverId}/certificate.pdf`,
          { headers: getAuthHeader(), responseType: 'blob' },
        );
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `Handover_${handoverForm.bike_plate_number || 'certificate'}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      };

      try {
        await downloadHandoverServer();
        toast.success('Motorcycle handover recorded successfully. PDF downloaded!', {
          action: { label: 'Download Again', onClick: downloadHandoverServer },
        });
      } catch (pdfError) {
        console.error('PDF generation error:', pdfError);
        toast.success('Motorcycle handover recorded successfully', {
          action: {
            label: 'Download PDF',
            onClick: async () => {
              try {
                await downloadHandoverServer();
                toast.success('PDF downloaded successfully');
              } catch (error) {
                toast.error('Failed to generate PDF');
              }
            }
          }
        });
      }

      // Optional second artefact — Asset Handover Form (in-house only).
      // Fires only when the operator ticked the checkbox on the form.
      // Failure here is non-fatal — the handover itself is already
      // recorded successfully.
      if (handoverForm.generate_asset_handover_form) {
        const handoverId = response.data?.id || handoverData.id;
        try {
          const r = await axios.post(
            `${API}/document-templates/render-for-handover/${handoverId}`,
            {},
            { headers: getAuthHeader(), responseType: 'blob' },
          );
          const url = URL.createObjectURL(
            new Blob([r.data], {
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            }),
          );
          const a = document.createElement('a');
          a.href = url;
          a.download = `Asset_Handover_${handoverForm.bike_plate_number || handoverId}.docx`;
          document.body.appendChild(a); a.click(); a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
          toast.success('Asset Handover Form downloaded');
        } catch (assetErr) {
          const msg = assetErr?.response?.data?.detail
            || 'Could not generate Asset Handover Form — check the template in Settings → Standard Documents.';
          toast.error(msg);
        }
      }
      
      setHandoverDialogOpen(false);
      setHandoverForm({
        lessee_name: '',
        bike_id: '',
        bike_plate_number: '',
        handover_date: new Date().toISOString().split('T')[0],
        handover_time: new Date().toTimeString().slice(0, 5),
        location: '',
        technician_name: user?.name || '',
        rider_id: '',
        rider_name: '',
        rider_emirates_id: '',
        rider_passport_no: '',
        rider_driving_license_no: '',
        rider_company_mobile: '',
        rider_personal_mobile: '',
        rider_emirates_id_front_url: '',
        rider_emirates_id_back_url: '',
        rider_driving_license_front_url: '',
        rider_driving_license_back_url: '',
        notes: '',
        handover_photos: [],
        generate_asset_handover_form: false,
        asset_handover_platform: '',
      });
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'rider_lessee_mismatch') {
        // Long, explicit message so the technician understands the mismatch.
        toast.error(detail.message, {
          duration: 10000,
          description: `${detail.rider_name}: registered with "${detail.rider_lessee_name || detail.rider_lessee_id || '—'}"`,
        });
      } else {
        toast.error(formatErrorMessage(error, 'Failed to record handover'));
      }
    } finally {
      setSubmittingHandover(false);
    }
  };

  const handleTakeoverSubmit = async (e) => {
    e.preventDefault();
    if (submittingTakeover) return;

    const bioState = takeoverForm.biometrics || {};
    if (!bioState.has_signature && !bioState.signature_url) {
      toast.error('Rider digital signature is required before recording the takeover.');
      return;
    }

    setSubmittingTakeover(true);
    try {
      const bio = await uploadBiometrics(takeoverForm.biometrics);
      const payload = {
        ...takeoverForm,
        rider_signature_url: bio.signature_url || null,
        rider_fingerprint_url: bio.fingerprint_url || null,
        rider_fingerprint_template_url: bio.fingerprint_template_url || null,
        rider_fingerprint_quality: bio.fingerprint_quality || null,
        rider_fingerprint_device_serial: bio.fingerprint_device_serial || null,
        biometrics_captured_at: bio.biometrics_captured_at || null,
      };
      delete payload.biometrics;
      const response = await axios.post(`${API}/motorcycles/takeover`, payload, {
        headers: getAuthHeader()
      });
      
      // Auto-download PDF immediately
      const takeoverData = { ...payload, id: response.data?.id || `TO-${Date.now()}` };

      const downloadTakeoverServer = async () => {
        const takeoverId = response.data?.id || takeoverData.id;
        const res = await axios.get(
          `${API}/motorcycles/takeovers/${takeoverId}/certificate.pdf`,
          { headers: getAuthHeader(), responseType: 'blob' },
        );
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `Takeover_${takeoverForm.bike_plate_number || 'certificate'}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      };

      try {
        await downloadTakeoverServer();
        toast.success('Motorcycle takeover recorded successfully. PDF downloaded!', {
          action: { label: 'Download Again', onClick: downloadTakeoverServer },
        });
      } catch (pdfError) {
        console.error('PDF generation error:', pdfError);
        toast.success('Motorcycle takeover recorded successfully', {
          action: {
            label: 'Download PDF',
            onClick: async () => {
              try {
                await downloadTakeoverServer();
                toast.success('PDF downloaded successfully');
              } catch (error) {
                toast.error('Failed to generate PDF');
              }
            }
          }
        });
      }
      
      setTakeoverDialogOpen(false);
      setTakeoverForm({
        lessee_name: '',
        bike_id: '',
        bike_plate_number: '',
        takeover_date: new Date().toISOString().split('T')[0],
        takeover_time: new Date().toTimeString().slice(0, 5),
        location: '',
        technician_name: user?.name || '',
        rider_name: '',
        rider_emirates_id: '',
        takeover_type: 'temporary',
        temporary_start_date: '',
        temporary_end_date: '',
        temporary_reason: '',
        defects: [],
        notes: '',
        takeover_photos: []
      });
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to record takeover'));
    } finally {
      setSubmittingTakeover(false);
    }
  };

  const handleAccidentSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/motorcycles/accident`, accidentForm, {
        headers: getAuthHeader()
      });
      
      // Auto-download PDF immediately
      const accidentData = { ...accidentForm, id: response.data?.id || `ACC-${Date.now()}` };
      const motorcycle = allMotorcycles.find(m => m.id === accidentForm.bike_id);
      
      try {
        await generateAccidentPDF(accidentData, motorcycle);
        toast.success('Accident report submitted. PDF downloaded!', {
          action: {
            label: 'Download Again',
            onClick: async () => {
              try {
                await generateAccidentPDF(accidentData, motorcycle);
              } catch (error) {
                toast.error('Failed to generate PDF');
              }
            }
          }
        });
      } catch (pdfError) {
        console.error('PDF generation error:', pdfError);
        toast.success('Accident report submitted successfully.', {
          action: {
            label: 'Download PDF',
            onClick: async () => {
              try {
                await generateAccidentPDF(accidentData, motorcycle);
                toast.success('PDF downloaded successfully');
              } catch (error) {
                toast.error('Failed to generate PDF');
              }
            }
          }
        });
      }
      
      setAccidentDialogOpen(false);
      setAccidentForm({
        accident_date: new Date().toISOString().split('T')[0],
        accident_time: new Date().toTimeString().slice(0, 5),
        bike_id: '',
        bike_plate_number: '',
        rider_name: '',
        location: '',
        police_report_type: 'green',
        police_report_photos: [],
        bike_photos: [],
        description: '',
        submit_insurance_claim: false,
        estimated_damage_cost: 0
      });
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to submit accident report'));
    }
  };

  const resetForm = () => {
    setFormData({
      plate_number: '',
      golden_plate_number: '',
      chassis_number: '',
      brand: '',
      model_name: '',
      model_version: '',
      color: '',
      model_year: new Date().getFullYear(),
      year_of_manufacturing: new Date().getFullYear(),
      doors: 0,
      seats: 1,
      origin: '',
      engine_number: '',
      status: 'available',
      lease_status: 'available',
      lessee_id: '',
      lessee_name: '',
      lessee_contact: '',
      monthly_lease_amount: 0,
      lease_start_date: '',
      registration_date: '',
      registration_expiry_date: '',
      insurance_type: '',
      insurance_provider: '',
      insurance_start_date: '',
      insurance_expiry_date: '',
      city: '',
      salik_tag_no: '',
      assigned_rider_id: '',
      assigned_rider_name: '',
      registration_card_url: ''
    });
    setEditingId(null);
    setRegistrationCardPreview(null);
    setAutoFilled(false);
    setIndividualAutoFilled({});
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'bg-green-100 text-green-800',
      'in-use': 'bg-blue-100 text-green-800',
      maintenance: 'bg-orange-100 text-orange-800',
      retired: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="motorcycles-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">{t('motorcycles.title')}</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">{t('motorcycles.subtitle')}</p>
          {activeFilter && (
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-green-600 text-white">
                {activeFilter === 'available' && t('dashboard.availableBikes')}
                {activeFilter === 'leased' && t('motorcycles.leasedMotorcycles')}
                {activeFilter === 'maintenance' && t('dashboard.bikesUnderMaintenance')}
                {activeFilter === 'insurance-expiring' && t('dashboard.insuranceExpiring')}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilter}
                className="h-6 px-2"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>
        <div className={`flex gap-1.5 items-center ${isMobile || isTablet ? 'flex-wrap' : 'flex-nowrap'}`}>
          {user?.role !== 'Account Manager' && (
            <>
              {/* Sync now runs automatically on page load */}
              
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-lime-600 to-green-600 text-xs px-2 py-1.5 h-8 whitespace-nowrap" size="sm" data-testid="add-motorcycle-btn">
                    <Plus className="w-3 h-3 mr-1" />
                    {isMobile ? 'Add' : t('motorcycles.addMotorcycle')}
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
                  <DialogHeader>
                    <DialogTitle>{editingId ? t('motorcycles.editMotorcycle') : t('motorcycles.addMotorcycle')}</DialogTitle>
                    <DialogDescription>{t('motorcycles.subtitle')}</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Registration Card Upload - Moved to Top */}
              <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Upload Registration Card (Mulkia)</h3>
                  {autoFilled && (
                    <div className="flex items-center gap-1 ml-auto">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-xs text-green-700 font-medium">Auto-filled</span>
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-blue-700">
                  Upload your UAE vehicle registration card to automatically fill form fields. The system will scan and extract vehicle information.
                </p>
                
                {/* Enhanced File Upload with OCR */}
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      id="registration-card-upload"
                      accept="image/jpeg,image/jpg,image/png"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            // Create temporary URL for the file
                            const tempUrl = URL.createObjectURL(file);
                            
                            // Process with OCR and upload to server
                            await processRegistrationCardOCR(tempUrl, file);
                          } catch (error) {
                            toast.error('Failed to process file');
                          }
                        }
                        e.target.value = ''; // Reset input
                      }}
                    />
                    
                    <div className="space-y-3">
                      {ocrProcessing ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader className="w-8 h-8 text-blue-500 animate-spin" />
                          <div className="text-sm font-medium text-blue-700">
                            🚀 AI Vision scanning registration card...
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-32 h-2 bg-blue-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-500 transition-all duration-1000"
                                style={{ width: `${(ocrCountdown / 30) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs font-mono text-blue-600">{ocrCountdown}s</span>
                          </div>
                          <div className="text-xs text-blue-500">
                            Extracting vehicle information
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-center">
                            <Upload className="w-12 h-12 text-blue-400" />
                          </div>
                          <div>
                            <label 
                              htmlFor="registration-card-upload"
                              className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                            >
                              Click to upload registration card
                            </label>
                            <p className="text-sm text-gray-500">or drag and drop</p>
                          </div>
                          <p className="text-xs text-gray-400">
                            Supports: JPG, PNG (Max 10MB)
                          </p>
                        </>
                      )}
                    </div>
                    
                    {/* Display uploaded image preview */}
                    {(formData.registration_card_url || registrationCardPreview) && !ocrProcessing && (
                      <div className="mt-4 p-3 bg-white rounded border">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span className="text-sm font-medium text-green-700">Registration card uploaded</span>
                          </div>
                          {editingId && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setRegistrationCardPreview(null);
                                setFormData({ ...formData, registration_card_url: '' });
                                document.getElementById('registration-card-upload')?.click();
                              }}
                              className="text-xs"
                            >
                              Re-upload
                            </Button>
                          )}
                        </div>
                        {/* Image Preview */}
                        <div className="relative">
                          <img 
                            src={registrationCardPreview || formData.registration_card_url}
                            alt="Registration Card Preview"
                            className="w-full max-h-32 object-contain rounded border"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <a 
                            href={registrationCardPreview || formData.registration_card_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute top-2 right-2 bg-white p-1 rounded shadow hover:bg-gray-100"
                            title="View full size"
                          >
                            <Eye className="w-4 h-4 text-gray-600" />
                          </a>
                        </div>
                        {autoFilled && (
                          <p className="text-xs text-gray-600 mt-2">
                            Form fields have been auto-filled. Use &quot;Clear&quot; buttons to edit specific fields.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* OCR Results Summary */}
                  {ocrResults && (
                    <div className="bg-white p-3 rounded border border-green-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Extracted Information:</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {ocrResults.plate_number && (
                          <div><span className="text-gray-500">Plate:</span> {ocrResults.plate_number}</div>
                        )}
                        {ocrResults.chassis_number && (
                          <div><span className="text-gray-500">Chassis:</span> {ocrResults.chassis_number}</div>
                        )}
                        {ocrResults.model_name && (
                          <div><span className="text-gray-500">Model:</span> {ocrResults.model_name}</div>
                        )}
                        {ocrResults.color && (
                          <div><span className="text-gray-500">Color:</span> {ocrResults.color}</div>
                        )}
                        {ocrResults.city && (
                          <div><span className="text-gray-500">City:</span> {ocrResults.city}</div>
                        )}
                        {ocrResults.insurance_expiry_date && (
                          <div><span className="text-gray-500">Insurance Expiry:</span> {formatDateDisplay(ocrResults.insurance_expiry_date)}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Vehicle Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Vehicle Information</h3>
                
                <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label>{t('motorcycles.plateNumber')} *</Label>
                      {individualAutoFilled.plate_number && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                      )}
                    </div>
                    {individualAutoFilled.plate_number && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => clearIndividualField('plate_number')}
                        className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                  <Input
                    value={formData.plate_number}
                    onChange={(e) => {
                      let value = e.target.value.toUpperCase();
                      // Allow letters, numbers, spaces, and hyphens
                      value = value.replace(/[^A-Z0-9\s-]/g, '');
                      setFormData({ ...formData, plate_number: value });
                    }}
                    placeholder="DXB 2-33654"
                    pattern="[A-Z]{3}\s\d{1,2}-\d{5}"
                    title="Format: DXB 2-33654 (City Code, Category-Number)"
                    required
                    data-testid="motorcycle-plate"
                    className={individualAutoFilled.plate_number ? "border-green-300 bg-green-50" : ""}
                  />
                  <p className="text-xs text-slate-500">Format: DXB 2-33654 (Old Black &amp; White plate)</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label>Golden Plate Number</Label>
                    <span className="text-[10px] bg-yellow-300 text-black border border-yellow-600 rounded px-1.5 py-0.5 font-semibold">NEW</span>
                  </div>
                  <Input
                    value={formData.golden_plate_number}
                    onChange={(e) => {
                      let value = e.target.value.toUpperCase();
                      value = value.replace(/[^A-Z0-9\s-]/g, '');
                      setFormData({ ...formData, golden_plate_number: value });
                    }}
                    placeholder="DXB 9-3354"
                    pattern="[A-Z]{3}\s\d{1,2}-\d{5}"
                    title="Format: DXB 9-3354 (Golden plate)"
                    data-testid="motorcycle-golden-plate"
                    className="bg-yellow-50 border-yellow-300 focus-visible:ring-yellow-400"
                  />
                  <p className="text-xs text-slate-500">Optional. Once assigned, both plates are tracked as a pair.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label>{t('motorcycles.chassisNumber')} *</Label>
                      {individualAutoFilled.chassis_number && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                      )}
                    </div>
                    {individualAutoFilled.chassis_number && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => clearIndividualField('chassis_number')}
                        className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                  <Input
                    value={formData.chassis_number}
                    onChange={(e) => setFormData({ ...formData, chassis_number: e.target.value.toUpperCase() })}
                    placeholder="R3RSR3024PA000682"
                    maxLength={17}
                    minLength={17}
                    required
                    data-testid="motorcycle-chassis"
                    className={individualAutoFilled.chassis_number ? "border-green-300 bg-green-50" : ""}
                  />
                  <p className="text-xs text-slate-500">VIN must be exactly 17 characters</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label>{t('motorcycles.model')} *</Label>
                      {individualAutoFilled.model_name && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                      )}
                    </div>
                    {individualAutoFilled.model_name && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => clearIndividualField('model_name')}
                        className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                  <Input
                    value={formData.model_name}
                    onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                    placeholder="e.g., Tesla E-Bike"
                    required
                    data-testid="motorcycle-model-name"
                    className={individualAutoFilled.model_name ? "border-green-300 bg-green-50" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Version *</Label>
                  <Input
                    value={formData.model_version}
                    onChange={(e) => setFormData({ ...formData, model_version: e.target.value })}
                    placeholder="e.g., Pro, Standard, Elite"
                    required
                    data-testid="motorcycle-model-version"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label>{t('motorcycles.color')} *</Label>
                      {individualAutoFilled.color && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                      )}
                    </div>
                    {individualAutoFilled.color && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => clearIndividualField('color')}
                        className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="e.g., Black, White, Red"
                    required
                    data-testid="motorcycle-color"
                    className={individualAutoFilled.color ? "border-green-300 bg-green-50" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label>Model Year *</Label>
                      {individualAutoFilled.model_year && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                      )}
                    </div>
                    {individualAutoFilled.model_year && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => clearIndividualField('model_year', new Date().getFullYear())}
                        className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                  <Input
                    type="number"
                    value={formData.model_year || formData.year_of_manufacturing}
                    onChange={(e) => setFormData({ ...formData, model_year: parseInt(e.target.value), year_of_manufacturing: parseInt(e.target.value) })}
                    min="2000"
                    max={new Date().getFullYear() + 1}
                    required
                    data-testid="motorcycle-year"
                  />
                </div>
                {/* Brand */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label>Brand</Label>
                      {individualAutoFilled.brand && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                      )}
                    </div>
                  </div>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="e.g., YADEA, NIU, BMW"
                    data-testid="motorcycle-brand"
                    className={individualAutoFilled.brand ? "border-green-300 bg-green-50" : ""}
                  />
                </div>
                {/* Engine Number */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label>Engine No.</Label>
                      {individualAutoFilled.engine_number && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                      )}
                    </div>
                  </div>
                  <Input
                    value={formData.engine_number}
                    onChange={(e) => setFormData({ ...formData, engine_number: e.target.value.toUpperCase() })}
                    placeholder="Engine number"
                    data-testid="motorcycle-engine"
                    className={individualAutoFilled.engine_number ? "border-green-300 bg-green-50" : ""}
                  />
                </div>
                {/* Origin */}
                <div className="space-y-2">
                  <Label>Origin</Label>
                  <Select value={formData.origin} onValueChange={(value) => setFormData({ ...formData, origin: value })}>
                    <SelectTrigger data-testid="motorcycle-origin">
                      <SelectValue placeholder="Select origin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="India">India</SelectItem>
                      <SelectItem value="China">China</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Doors */}
                <div className="space-y-2">
                  <Label>Doors</Label>
                  <Input
                    type="number"
                    value={formData.doors}
                    onChange={(e) => setFormData({ ...formData, doors: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="5"
                    data-testid="motorcycle-doors"
                  />
                </div>
                {/* Seats */}
                <div className="space-y-2">
                  <Label>Seats</Label>
                  <Input
                    type="number"
                    value={formData.seats}
                    onChange={(e) => setFormData({ ...formData, seats: parseInt(e.target.value) || 1 })}
                    min="1"
                    max="10"
                    data-testid="motorcycle-seats"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('motorcycles.status')} *</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })} required>
                    <SelectTrigger data-testid="motorcycle-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">{t('motorcycles.statusAvailable')}</SelectItem>
                      <SelectItem value="in-use">{t('motorcycles.statusInUse')}</SelectItem>
                      <SelectItem value="maintenance">{t('motorcycles.statusMaintenance')}</SelectItem>
                      <SelectItem value="retired">{t('motorcycles.statusRetired')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Lease Status *</Label>
                  <Select value={formData.lease_status} onValueChange={(value) => setFormData({ ...formData, lease_status: value })} required>
                    <SelectTrigger data-testid="motorcycle-lease-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">{t('motorcycles.leaseAvailable')}</SelectItem>
                      <SelectItem value="leased">{t('motorcycles.leaseLeased')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Registration Section */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-semibold text-gray-700">Registration Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label>Registration Date</Label>
                        {individualAutoFilled.registration_date && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                        )}
                      </div>
                    </div>
                    <Input
                      type="date"
                      value={formData.registration_date}
                      onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })}
                      data-testid="motorcycle-reg-date"
                      className={individualAutoFilled.registration_date ? "border-green-300 bg-green-50" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label>Registration Expiry</Label>
                        {individualAutoFilled.registration_expiry_date && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                        )}
                      </div>
                    </div>
                    <Input
                      type="date"
                      value={formData.registration_expiry_date}
                      onChange={(e) => setFormData({ ...formData, registration_expiry_date: e.target.value })}
                      data-testid="motorcycle-reg-expiry"
                      className={individualAutoFilled.registration_expiry_date ? "border-green-300 bg-green-50" : ""}
                    />
                  </div>
                </div>
              </div>
              
              {/* Insurance Section */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-semibold text-gray-700">Insurance Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Insurance Type *</Label>
                    <Select value={formData.insurance_type} onValueChange={(value) => setFormData({ ...formData, insurance_type: value })} required>
                      <SelectTrigger data-testid="motorcycle-insurance-type">
                        <SelectValue placeholder="Select insurance type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Comprehensive">Comprehensive</SelectItem>
                        <SelectItem value="Third Party Only">Third Party Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label>{t('motorcycles.insuranceProvider')} *</Label>
                        {individualAutoFilled.insurance_provider && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                        )}
                      </div>
                    </div>
                    <Input
                      value={formData.insurance_provider}
                      onChange={(e) => setFormData({ ...formData, insurance_provider: e.target.value })}
                      placeholder="e.g., AXA, Zurich"
                      required
                      data-testid="motorcycle-insurance-provider"
                      className={individualAutoFilled.insurance_provider ? "border-green-300 bg-green-50" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Insurance Start *</Label>
                    <Input
                      type="date"
                      value={formData.insurance_start_date}
                      onChange={(e) => setFormData({ ...formData, insurance_start_date: e.target.value })}
                      required
                      data-testid="motorcycle-insurance-start"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Insurance Expiry *</Label>
                    <Input
                      type="date"
                      value={formData.insurance_expiry_date}
                      onChange={(e) => setFormData({ ...formData, insurance_expiry_date: e.target.value })}
                      required
                      data-testid="motorcycle-insurance-expiry"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label>{t('motorcycles.city')} *</Label>
                      {individualAutoFilled.city && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                      )}
                    </div>
                    {individualAutoFilled.city && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => clearIndividualField('city')}
                        className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                  <SearchableSelect
                    options={[
                      { value: 'Dubai', label: t('cities.dubai') },
                      { value: 'Abu Dhabi', label: t('cities.abuDhabi') },
                      { value: 'Sharjah', label: t('cities.sharjah') },
                      { value: 'Ajman', label: t('cities.ajman') },
                      { value: 'Umm Al Quwain', label: t('cities.ummAlQuwain') },
                      { value: 'Ras Al Khaimah', label: t('cities.rasAlKhaimah') },
                      { value: 'Fujairah', label: t('cities.fujairah') }
                    ]}
                    value={formData.city}
                    onValueChange={(value) => setFormData({ ...formData, city: value })}
                    placeholder="Select Emirates"
                    showMetadata={false}
                    required
                    className={individualAutoFilled.city ? "border-green-300 bg-green-50" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('motorcycles.salikTag')} *</Label>
                  <Input
                    type="text"
                    value={formData.salik_tag_no}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                      if (value.length <= 8) {
                        setFormData({ ...formData, salik_tag_no: value });
                      }
                    }}
                    placeholder="12345678"
                    maxLength={8}
                    pattern="[0-9]{8}"
                    required
                    data-testid="motorcycle-salik-tag"
                  />
                  <p className="text-xs text-slate-500">Must be exactly 8 digits</p>
                </div>
              </div>
              </div>

              
              {(permissions.canEdit || user?.role === 'Fleet Manager') && (
                <div className="space-y-2">
                  <Label>{t('motorcycles.assignedTo')}</Label>
                  <Select value={formData.assigned_rider_id || "unassigned"} onValueChange={handleRiderChange}>
                    <SelectTrigger data-testid="motorcycle-rider">
                      <SelectValue placeholder="Select rider to assign" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">None (Unassigned)</SelectItem>
                      {riders.map((rider) => (
                        <SelectItem key={rider.id} value={rider.id}>
                          {rider.name} - {rider.emirates_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Assign a rider to this motorcycle</p>
                </div>
              )}

              {formData.lease_status === 'leased' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Select Lessee Company *</Label>
                    <SearchableSelect
                      options={lessees.map((lessee) => ({
                        value: lessee.id,
                        label: lessee.company_name,
                        searchText: `${lessee.company_name} ${lessee.trade_license_no || ''} ${lessee.owner_name || ''} ${lessee.contact_mobile || ''}`,
                        metadata: {
                          'Trade License': lessee.trade_license_no || lessee.trade_license_number,
                          'Contact': lessee.contact_mobile || lessee.owner_mobile,
                          'Owner': lessee.owner_name
                        }
                      }))}
                      value={formData.lessee_id}
                      onValueChange={handleLesseeSelect}
                      placeholder="Select lessee company"
                      emptyMessage="No lessees registered"
                      required
                    />
                    {formData.lessee_name && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg mt-2">
                        <p className="text-sm font-medium text-green-900">
                          <span className="text-xs text-green-600">Lessee:</span> {formData.lessee_name}
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          <span className="text-green-600">Contact:</span> {formData.lessee_contact}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Lease Amount ($)</Label>
                    <Input
                      type="number"
                      value={formData.monthly_lease_amount}
                      onChange={(e) => setFormData({ ...formData, monthly_lease_amount: parseFloat(e.target.value) })}
                      data-testid="motorcycle-lease-amount"
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Lease Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.lease_start_date}
                      onChange={(e) => setFormData({ ...formData, lease_start_date: e.target.value })}
                      required
                      data-testid="motorcycle-lease-start"
                    />
                  </div>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-lime-600 to-green-600" data-testid="submit-motorcycle-btn">
                  {editingId ? t('common.edit') : t('common.add')}
                </Button>
              </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Motorcycle Handover Button */}
              <Dialog open={handoverDialogOpen} onOpenChange={(o) => { if (!submittingHandover) setHandoverDialogOpen(o); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50 text-xs px-2 py-1.5 h-8 whitespace-nowrap" size="sm">
                    <HandMetal className="w-3 h-3 mr-1" />
                    {isMobile ? '' : 'Handover'}
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
                  <DialogHeader>
                    <DialogTitle>Motorcycle Handover to Lessee</DialogTitle>
                    <DialogDescription>Record motorcycle handover details</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleHandoverSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Lessee *</Label>
                        <SearchableSelect
                          options={lessees.map(lessee => ({
                            value: lessee.company_name,
                            label: lessee.company_name,
                            searchText: `${lessee.company_name} ${lessee.owner_name || ''} ${lessee.trade_license_no || ''}`,
                            metadata: {
                              'Owner': lessee.owner_name,
                              'Trade License': lessee.trade_license_no || lessee.trade_license_number
                            }
                          }))}
                          value={handoverForm.lessee_name}
                          onValueChange={(value) => {
                            const lessee = lessees.find(l => l.company_name === value);
                            setHandoverForm({...handoverForm, lessee_name: value});
                          }}
                          placeholder="Select lessee"
                          emptyMessage="No lessees found"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Motorcycle *</Label>
                        <SearchableSelect
                          options={allMotorcycles.filter(m => m.lease_status === 'available').map(bike => ({
                            value: bike.id,
                            label: bike.golden_plate_number ? `${bike.plate_number} / ${bike.golden_plate_number}` : bike.plate_number,
                            searchText: `${bike.plate_number} ${bike.golden_plate_number || ''} ${bike.model_name} ${bike.chassis_number}`,
                            metadata: {
                              'Model': bike.model_name,
                              'Chassis': bike.chassis_number,
                              'City': bike.city || '-'
                            }
                          }))}
                          value={handoverForm.bike_id}
                          onValueChange={(value) => {
                            const bike = allMotorcycles.find(m => m.id === value);
                            let bikeData = { bike_id: value, bike_plate_number: bike?.plate_number || '' };
                            
                            // Auto-fill assigned rider if exists
                            if (bike && (bike.assigned_rider_id || bike.rider_id)) {
                              const assignedRiderId = bike.assigned_rider_id || bike.rider_id;
                              const assignedRider = riders.find(r => r.id === assignedRiderId);
                              if (assignedRider) {
                                bikeData.rider_name = assignedRider.name;
                                bikeData.rider_emirates_id = assignedRider.emirates_id || '';
                                bikeData.rider_id = assignedRider.id;
                                toast.success(`Auto-filled rider: ${assignedRider.name}`);
                              }
                            }
                            
                            setHandoverForm({...handoverForm, ...bikeData});
                          }}
                          placeholder="Select motorcycle"
                          emptyMessage="No available motorcycles"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Handover Date *</Label>
                        <Input type="date" value={handoverForm.handover_date} onChange={(e) => setHandoverForm({...handoverForm, handover_date: e.target.value})} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Handover Time *</Label>
                        <Input type="time" value={handoverForm.handover_time} onChange={(e) => setHandoverForm({...handoverForm, handover_time: e.target.value})} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Location *</Label>
                        <Select value={handoverForm.location} onValueChange={(value) => setHandoverForm({...handoverForm, location: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                          <SelectContent>
                            {swapLocations.map(loc => (
                              <SelectItem key={loc.id} value={loc.name}>{loc.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Technician Name *</Label>
                        <Select value={handoverForm.technician_name} onValueChange={(value) => setHandoverForm({...handoverForm, technician_name: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select technician" />
                          </SelectTrigger>
                          <SelectContent>
                            {users.filter(u => u.role === 'Repair Technician').map(tech => (
                              <SelectItem key={tech.id} value={tech.name}>{tech.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Rider Name *</Label>
                        <SearchableSelect
                          options={
                            // Filter to riders belonging to the selected lessee.
                            // A rider with no lessee registered (new hires) is
                            // shown to everyone — same rule the backend uses.
                            (() => {
                              const selLessee = (handoverForm.lessee_name || '').trim().toLowerCase();
                              const filtered = selLessee
                                ? riders.filter(r => {
                                    const rl = (r.lessee_name || '').trim().toLowerCase();
                                    return !rl || rl === selLessee;
                                  })
                                : riders;
                              return filtered.map(rider => ({
                                value: rider.name,
                                label: rider.name,
                                searchText: `${rider.name} ${rider.emirates_id} ${rider.mobile_uae || ''}`,
                                metadata: {
                                  'Emirates ID': rider.emirates_id,
                                  'Mobile': rider.mobile_uae,
                                  'License': rider.license_number,
                                  'Employer': rider.lessee_name || '(no lessee registered)',
                                },
                              }));
                            })()
                          }
                          value={handoverForm.rider_name}
                          onValueChange={(value) => {
                            const rider = riders.find(r => r.name === value);
                            setHandoverForm({
                              ...handoverForm,
                              rider_name: value,
                              rider_emirates_id: rider?.emirates_id || '',
                              rider_id: rider?.id || '',
                              // Auto-fill the rest of the rider identity so
                              // the technician only has to verify + fix.
                              rider_passport_no: rider?.passport_number || '',
                              rider_driving_license_no: rider?.uae_driving_license_no || rider?.license_number || '',
                              rider_company_mobile: rider?.mobile_uae || rider?.phone || '',
                            });
                          }}
                          placeholder={handoverForm.lessee_name ? 'Select rider' : 'Select lessee first'}
                          emptyMessage={handoverForm.lessee_name ? `No riders registered with ${handoverForm.lessee_name}` : 'Select a lessee first'}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rider Emirates ID *</Label>
                        <Input
                          value={handoverForm.rider_emirates_id}
                          onChange={(e) => setHandoverForm({...handoverForm, rider_emirates_id: e.target.value})}
                          placeholder="784-XXXX-XXXXXXX-X"
                          data-testid="handover-rider-eid"
                        />
                        <p className="text-xs text-slate-500">Verify with rider and correct if needed</p>
                      </div>
                    </div>

                    {/* Extra rider identity — auto-filled from rider record, editable */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Passport No</Label>
                        <Input
                          value={handoverForm.rider_passport_no}
                          onChange={(e) => setHandoverForm({...handoverForm, rider_passport_no: e.target.value})}
                          placeholder="A1234567"
                          data-testid="handover-rider-passport"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Driving License No</Label>
                        <Input
                          value={handoverForm.rider_driving_license_no}
                          onChange={(e) => setHandoverForm({...handoverForm, rider_driving_license_no: e.target.value})}
                          placeholder="4980482"
                          data-testid="handover-rider-dl"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Company Mobile No</Label>
                        <Input
                          value={handoverForm.rider_company_mobile}
                          onChange={(e) => setHandoverForm({...handoverForm, rider_company_mobile: e.target.value})}
                          placeholder="+971 5X XXX XXXX"
                          data-testid="handover-rider-company-mobile"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Personal Mobile No</Label>
                        <Input
                          value={handoverForm.rider_personal_mobile}
                          onChange={(e) => setHandoverForm({...handoverForm, rider_personal_mobile: e.target.value})}
                          placeholder="+971 5X XXX XXXX"
                          data-testid="handover-rider-personal-mobile"
                        />
                      </div>
                    </div>

                    {/* Emirates ID + Driving License uploads (page 2 of certificate) */}
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: 'rider_emirates_id_front_url', label: 'Emirates ID – Front' },
                        { key: 'rider_emirates_id_back_url', label: 'Emirates ID – Back' },
                        { key: 'rider_driving_license_front_url', label: 'Driving License – Front' },
                        { key: 'rider_driving_license_back_url', label: 'Driving License – Back' },
                      ].map(({ key, label }) => (
                        <div key={key} className="space-y-1">
                          <Label className="text-xs">{label}</Label>
                          <Input
                            type="file"
                            accept="image/*,.pdf"
                            data-testid={`handover-${key}`}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = () => {
                                setHandoverForm(prev => ({ ...prev, [key]: reader.result }));
                              };
                              reader.readAsDataURL(file);
                            }}
                          />
                          {handoverForm[key] && (
                            <p className="text-xs text-green-600">✓ Uploaded</p>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>ODO Reading (KM)</Label>
                        <Input type="number" min="0" value={handoverForm.odometer_km} onChange={(e) => setHandoverForm({...handoverForm, odometer_km: e.target.value})} placeholder="e.g. 12450" data-testid="handover-odometer" />
                      </div>
                      <div className="space-y-2">
                        <Label>Notes</Label>
                        <Input value={handoverForm.notes} onChange={(e) => setHandoverForm({...handoverForm, notes: e.target.value})} placeholder="Additional notes" />
                      </div>
                    </div>
                    
                    {/* Handover Photos */}
                    <MultiPhotoUpload
                      photos={handoverForm.handover_photos}
                      onChange={(photos) => setHandoverForm({ ...handoverForm, handover_photos: photos })}
                      label="Handover Photos"
                      helperText="Upload multiple photos taken during handover"
                      API={API}
                      getAuthHeader={getAuthHeader}
                    />

                    {/* Generate Asset Handover Form — in-house only.
                        On tick we validate the currently-selected lessee's
                        type; if external, we pop a toast and untick. When
                        in-house, we auto-populate the platform from the
                        selected rider's record. On submit, we generate
                        the docx via /api/document-templates/render-for-handover
                        and offer it as a second download. */}
                    <div className="border rounded-md bg-amber-50/40 border-amber-200 p-3">
                      <label className="flex items-start gap-2 cursor-pointer" data-testid="handover-generate-asset-toggle">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={handoverForm.generate_asset_handover_form}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) {
                              // Resolve lessee type from the loaded lessees state.
                              const selLessee = (handoverForm.lessee_name || '').trim().toLowerCase();
                              const lesseeRow = lessees.find(l =>
                                (l.company_name || '').trim().toLowerCase() === selLessee
                                || (l.name || '').trim().toLowerCase() === selLessee
                              );
                              const lesseeType = (lesseeRow?.lessee_type || 'external').toLowerCase();
                              if (lesseeType !== 'in-house' && lesseeType !== 'in_house' && lesseeType !== 'inhouse') {
                                toast.error('This option is not applicable for external lessee');
                                return;
                              }
                              // Auto-populate platform from the rider.
                              const riderRow = riders.find(r =>
                                r.id === handoverForm.rider_id
                                || r.name === handoverForm.rider_name
                              );
                              const platform = riderRow?.platform || '';
                              setHandoverForm(prev => ({
                                ...prev,
                                generate_asset_handover_form: true,
                                asset_handover_platform: platform,
                              }));
                            } else {
                              setHandoverForm(prev => ({
                                ...prev,
                                generate_asset_handover_form: false,
                                asset_handover_platform: '',
                              }));
                            }
                          }}
                        />
                        <span>
                          <span className="font-medium text-amber-900">Generate Asset Handover Form</span>
                          <span className="block text-[11px] text-amber-800/80">
                            Renders the Word template configured in Settings → Standard Documents (target: Handover). In-house lessees only.
                          </span>
                        </span>
                      </label>
                      {handoverForm.generate_asset_handover_form && (
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                          <div>
                            <label className="text-xs text-slate-600">Platform (auto-populated from rider)</label>
                            <Input
                              value={handoverForm.asset_handover_platform || ''}
                              readOnly
                              placeholder="—"
                              className="bg-slate-50"
                              data-testid="handover-asset-platform"
                            />
                          </div>
                          <div className="text-[11px] text-slate-500 self-end pb-1.5">
                            The rendered .docx will download automatically once you click <b>Record Handover</b>.
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Rider signature + thumb-print capture */}
                    <BiometricCapture
                      value={handoverForm.biometrics}
                      onChange={(updater) => setHandoverForm(prev => ({
                        ...prev,
                        biometrics: typeof updater === 'function' ? updater(prev.biometrics) : updater,
                      }))}
                      formType="handover"
                      employeeId={handoverForm.rider_id}
                      riderName={handoverForm.rider_name}
                    />
                    
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setHandoverDialogOpen(false)} disabled={submittingHandover}>Cancel</Button>
                      <Button
                        type="submit"
                        className="modern-btn-primary bg-gradient-to-r from-lime-600 to-green-600"
                        disabled={submittingHandover}
                        data-testid="record-handover-btn"
                      >
                        {submittingHandover ? (
                          <><Loader className="w-4 h-4 mr-2 animate-spin" /> Recording…</>
                        ) : 'Record Handover'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Motorcycle Takeover Button */}
              <Dialog open={takeoverDialogOpen} onOpenChange={(o) => { if (!submittingTakeover) setTakeoverDialogOpen(o); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-orange-500 text-orange-600 hover:bg-orange-50 text-xs px-2 py-1.5 h-8 whitespace-nowrap" size="sm">
                    <TrendingDown className="w-3 h-3 mr-1" />
                    {isMobile ? '' : 'Takeover'}
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
                  <DialogHeader>
                    <DialogTitle>Motorcycle Takeover from Lessee</DialogTitle>
                    <DialogDescription>Record motorcycle takeover details</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleTakeoverSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Lessee *</Label>
                        <SearchableSelect
                          options={lessees.map(lessee => ({
                            value: lessee.company_name,
                            label: lessee.company_name,
                            searchText: `${lessee.company_name} ${lessee.owner_name || ''} ${lessee.trade_license_no || ''}`,
                            metadata: {
                              'Owner': lessee.owner_name,
                              'Trade License': lessee.trade_license_no || lessee.trade_license_number
                            }
                          }))}
                          value={takeoverForm.lessee_name}
                          onValueChange={(value) => setTakeoverForm({...takeoverForm, lessee_name: value})}
                          placeholder="Select lessee"
                          emptyMessage="No lessees found"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Motorcycle *</Label>
                        <SearchableSelect
                          options={allMotorcycles.filter(m => m.lease_status === 'leased').map(bike => ({
                            value: bike.id,
                            label: bike.golden_plate_number ? `${bike.plate_number} / ${bike.golden_plate_number}` : bike.plate_number,
                            searchText: `${bike.plate_number} ${bike.golden_plate_number || ''} ${bike.model_name} ${bike.chassis_number}`,
                            metadata: {
                              'Model': bike.model_name,
                              'Chassis': bike.chassis_number,
                              'City': bike.city || '-'
                            }
                          }))}
                          value={takeoverForm.bike_id}
                          onValueChange={async (value) => {
                            const bike = allMotorcycles.find(m => m.id === value);
                            let bikeData = { bike_id: value, bike_plate_number: bike?.plate_number || '' };

                            // Auto-fill assigned rider if the bike has one.
                            const assignedRiderId = bike?.assigned_rider_id || bike?.rider_id;
                            if (bike && assignedRiderId) {
                              // 1) Fast path — local riders state.
                              const localRider = riders.find(r => r.id === assignedRiderId);
                              if (localRider) {
                                bikeData.rider_name = localRider.name;
                                bikeData.rider_emirates_id = localRider.emirates_id || '';
                                bikeData.rider_id = localRider.id;
                                toast.success(`Auto-filled rider: ${localRider.name}`);
                              } else {
                                // 2) Fallback — trust the bike's stored `assigned_rider_name`
                                //    and fetch Emirates ID via cross-collection lookup
                                //    (rider may live in payroll_riders / staff_employees /
                                //    tap_candidates rather than the `riders` collection).
                                if (bike.assigned_rider_name || bike.rider_name) {
                                  bikeData.rider_name = bike.assigned_rider_name || bike.rider_name;
                                  bikeData.rider_id = assignedRiderId;
                                }
                                setTakeoverForm(prev => ({ ...prev, ...bikeData }));
                                try {
                                  const r = await axios.get(
                                    `${API}/riders/lookup/${assignedRiderId}`,
                                    { headers: getAuthHeader() },
                                  );
                                  const identity = r.data || {};
                                  if (identity.name || identity.emirates_id) {
                                    setTakeoverForm(prev => ({
                                      ...prev,
                                      rider_name: prev.rider_name || identity.name || '',
                                      rider_emirates_id: prev.rider_emirates_id || identity.emirates_id || '',
                                      rider_id: assignedRiderId,
                                    }));
                                    toast.success(`Auto-filled rider: ${identity.name || bikeData.rider_name}`);
                                  }
                                } catch (err) {
                                  // Silent — user can still fill Rider Name manually.
                                }
                                return;
                              }
                            }

                            setTakeoverForm({...takeoverForm, ...bikeData});
                          }}
                          placeholder="Select motorcycle"
                          emptyMessage="No leased motorcycles"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Takeover Date *</Label>
                        <Input type="date" value={takeoverForm.takeover_date} onChange={(e) => setTakeoverForm({...takeoverForm, takeover_date: e.target.value})} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Takeover Time *</Label>
                        <Input type="time" value={takeoverForm.takeover_time} onChange={(e) => setTakeoverForm({...takeoverForm, takeover_time: e.target.value})} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Location *</Label>
                        <Select value={takeoverForm.location} onValueChange={(value) => setTakeoverForm({...takeoverForm, location: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                          <SelectContent>
                            {swapLocations.map(loc => (
                              <SelectItem key={loc.id} value={loc.name}>{loc.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Technician Name *</Label>
                        <Select value={takeoverForm.technician_name} onValueChange={(value) => setTakeoverForm({...takeoverForm, technician_name: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select technician" />
                          </SelectTrigger>
                          <SelectContent>
                            {users.filter(u => u.role === 'Repair Technician').map(tech => (
                              <SelectItem key={tech.id} value={tech.name}>{tech.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Rider Name *</Label>
                        <SearchableSelect
                          options={riders.map(rider => ({
                            value: rider.name,
                            label: rider.name,
                            searchText: `${rider.name} ${rider.emirates_id} ${rider.mobile_uae || ''}`,
                            metadata: {
                              'Emirates ID': rider.emirates_id,
                              'Mobile': rider.mobile_uae,
                              'License': rider.license_number
                            }
                          }))}
                          value={takeoverForm.rider_name}
                          onValueChange={(value) => {
                            const rider = riders.find(r => r.name === value);
                            setTakeoverForm({
                              ...takeoverForm, 
                              rider_name: value,
                              rider_emirates_id: rider?.emirates_id || '',
                              rider_id: rider?.id || ''
                            });
                          }}
                          placeholder="Select rider"
                          emptyMessage="No riders found"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rider Emirates ID *</Label>
                        <Input value={takeoverForm.rider_emirates_id} readOnly className="bg-slate-50" />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label>Takeover Type *</Label>
                        <Select value={takeoverForm.takeover_type} onValueChange={(value) => setTakeoverForm({...takeoverForm, takeover_type: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="temporary">Temporary Submission</SelectItem>
                            <SelectItem value="permanent">Permanent Takeover</SelectItem>
                            <SelectItem value="maintenance">Submission for Maintenance</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {takeoverForm.takeover_type === 'temporary' && (
                        <>
                          <div className="space-y-2">
                            <Label>Temporary Start Date *</Label>
                            <Input type="date" value={takeoverForm.temporary_start_date} onChange={(e) => setTakeoverForm({...takeoverForm, temporary_start_date: e.target.value})} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Temporary End Date *</Label>
                            <Input type="date" value={takeoverForm.temporary_end_date} onChange={(e) => setTakeoverForm({...takeoverForm, temporary_end_date: e.target.value})} required />
                          </div>
                          <div className="space-y-2 col-span-2">
                            <Label>Reason for Temporary Submission *</Label>
                            <Input value={takeoverForm.temporary_reason} onChange={(e) => setTakeoverForm({...takeoverForm, temporary_reason: e.target.value})} placeholder="e.g., Rider on leave, Temporary parking" required />
                          </div>
                        </>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>ODO Reading (KM)</Label>
                        <Input type="number" min="0" value={takeoverForm.odometer_km} onChange={(e) => setTakeoverForm({...takeoverForm, odometer_km: e.target.value})} placeholder="e.g. 12450" data-testid="takeover-odometer" />
                      </div>
                      <div className="space-y-2">
                        <Label>Notes</Label>
                        <Input value={takeoverForm.notes} onChange={(e) => setTakeoverForm({...takeoverForm, notes: e.target.value})} placeholder="Additional notes" />
                      </div>

                      {/* Defects & Damages — searchable dropdown from repair-items catalogue */}
                      <div className="space-y-2 col-span-2">
                        <Label>Defects &amp; Damages</Label>
                        <Input
                          type="text"
                          placeholder="Search repair items (item code / description / category)…"
                          value={defectSearch}
                          onChange={(e) => setDefectSearch(e.target.value)}
                          data-testid="takeover-defect-search"
                        />
                        {defectSearch.trim().length > 0 && (
                          <div className="max-h-52 overflow-auto border rounded-md bg-white shadow-sm" data-testid="takeover-defect-results">
                            {(() => {
                              const q = defectSearch.trim().toLowerCase();
                              const matches = (repairItems || []).filter((ri) => {
                                const hay = [ri.item_code, ri.name, ri.description, ri.category, ri.amc_type, ri.type].join(' ').toLowerCase();
                                return hay.includes(q);
                              }).slice(0, 40);
                              if (matches.length === 0) {
                                return <div className="px-3 py-2 text-xs text-slate-500">No matching repair items</div>;
                              }
                              return matches.map((ri) => {
                                const alreadyAdded = (takeoverForm.defects || []).some(
                                  (d) => (d.item_code && d.item_code === ri.item_code) || d.name === (ri.description || ri.name),
                                );
                                return (
                                  <button
                                    type="button"
                                    key={ri.id || ri.item_code || ri.name}
                                    disabled={alreadyAdded}
                                    className={`w-full text-left px-3 py-2 text-xs border-b last:border-b-0 flex items-center justify-between gap-2 ${alreadyAdded ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'hover:bg-blue-50'}`}
                                    data-testid={`takeover-defect-option-${ri.item_code || ri.id}`}
                                    onClick={() => {
                                      const defect = {
                                        item_code: ri.item_code || '',
                                        name: ri.description || ri.name || '',
                                        category: ri.category || '',
                                        rate: Number(ri.rate) || 0,
                                      };
                                      setTakeoverForm({
                                        ...takeoverForm,
                                        defects: [...(takeoverForm.defects || []), defect],
                                      });
                                      setDefectSearch('');
                                    }}
                                  >
                                    <span className="flex-1 truncate">
                                      <span className="font-mono text-slate-500">{ri.item_code || '—'}</span>
                                      {' · '}{ri.description || ri.name}
                                      {ri.category && <span className="text-slate-400"> ({ri.category})</span>}
                                    </span>
                                    <span className="text-green-700 font-semibold whitespace-nowrap">DH {Number(ri.rate) || 0}</span>
                                  </button>
                                );
                              });
                            })()}
                          </div>
                        )}
                        {(takeoverForm.defects || []).length > 0 && (
                          <div className="border rounded-md overflow-hidden mt-2">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="text-left px-2 py-1.5 w-24">Item Code</th>
                                  <th className="text-left px-2 py-1.5">Description</th>
                                  <th className="text-left px-2 py-1.5 w-28">Category</th>
                                  <th className="text-right px-2 py-1.5 w-24">Rate (DH)</th>
                                  <th className="w-8"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {(takeoverForm.defects || []).map((d, idx) => (
                                  <tr key={idx} className="border-t" data-testid={`takeover-defect-row-${idx}`}>
                                    <td className="px-2 py-1.5 font-mono text-slate-600">{d.item_code || '—'}</td>
                                    <td className="px-2 py-1.5">{d.name}</td>
                                    <td className="px-2 py-1.5 text-slate-600">{d.category || '—'}</td>
                                    <td className="px-2 py-1.5 text-right text-green-700 font-semibold">{d.rate || 0}</td>
                                    <td className="px-2 py-1.5 text-right">
                                      <button
                                        type="button"
                                        className="text-red-600 hover:text-red-800"
                                        onClick={() => setTakeoverForm({
                                          ...takeoverForm,
                                          defects: (takeoverForm.defects || []).filter((_, i) => i !== idx),
                                        })}
                                      >×</button>
                                    </td>
                                  </tr>
                                ))}
                                <tr className="bg-slate-50 font-semibold">
                                  <td colSpan={3} className="px-2 py-1.5 text-right">Total</td>
                                  <td className="px-2 py-1.5 text-right text-green-700">
                                    DH {(takeoverForm.defects || []).reduce((sum, d) => sum + (Number(d.rate) || 0), 0)}
                                  </td>
                                  <td />
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}
                        <p className="text-xs text-slate-500">Search repair items by code, description or category and click to add. Rates are picked from the Maintenance → Repair Items catalogue.</p>
                      </div>
                    </div>
                    
                    {/* Takeover Photos */}
                    <MultiPhotoUpload
                      photos={takeoverForm.takeover_photos}
                      onChange={(photos) => setTakeoverForm({ ...takeoverForm, takeover_photos: photos })}
                      label="Takeover Photos"
                      helperText="Upload multiple photos taken during takeover"
                      API={API}
                      getAuthHeader={getAuthHeader}
                    />

                    {/* Rider signature + thumb-print capture */}
                    <BiometricCapture
                      value={takeoverForm.biometrics}
                      onChange={(updater) => setTakeoverForm(prev => ({
                        ...prev,
                        biometrics: typeof updater === 'function' ? updater(prev.biometrics) : updater,
                      }))}
                      formType="takeover"
                      employeeId={takeoverForm.rider_id}
                      riderName={takeoverForm.rider_name}
                    />
                    
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setTakeoverDialogOpen(false)} disabled={submittingTakeover}>Cancel</Button>
                      <Button
                        type="submit"
                        className="modern-btn-primary bg-gradient-to-r from-lime-600 to-green-600"
                        disabled={submittingTakeover}
                        data-testid="record-takeover-btn"
                      >
                        {submittingTakeover ? (
                          <><Loader className="w-4 h-4 mr-2 animate-spin" /> Recording…</>
                        ) : 'Record Takeover'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Report Accident Button */}
              <Dialog open={accidentDialogOpen} onOpenChange={setAccidentDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-red-500 text-red-600 hover:bg-red-50 text-xs px-2 py-1.5 h-8 whitespace-nowrap" size="sm">
                    <AlertOctagon className="w-3 h-3 mr-1" />
                    {isMobile ? '' : 'Accident'}
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
                  <DialogHeader>
                    <DialogTitle>Report Motorcycle Accident</DialogTitle>
                    <DialogDescription>Submit accident report and create maintenance record</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAccidentSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Accident Date *</Label>
                        <Input type="date" value={accidentForm.accident_date} onChange={(e) => setAccidentForm({...accidentForm, accident_date: e.target.value})} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Accident Time *</Label>
                        <Input type="time" value={accidentForm.accident_time} onChange={(e) => setAccidentForm({...accidentForm, accident_time: e.target.value})} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Motorcycle *</Label>
                        <SearchableSelect
                          options={allMotorcycles.map(bike => ({
                            value: bike.id,
                            label: bike.golden_plate_number ? `${bike.plate_number} / ${bike.golden_plate_number}` : bike.plate_number,
                            searchText: `${bike.plate_number} ${bike.golden_plate_number || ''} ${bike.model_name} ${bike.chassis_number}`,
                            metadata: {
                              'Model': bike.model_name,
                              'Chassis': bike.chassis_number,
                              'City': bike.city || '-',
                              'Status': bike.status
                            }
                          }))}
                          value={accidentForm.bike_id}
                          onValueChange={(value) => {
                            const bike = allMotorcycles.find(m => m.id === value);
                            let bikeData = { bike_id: value, bike_plate_number: bike?.plate_number || '' };
                            
                            // Auto-fill assigned rider if exists
                            if (bike && (bike.assigned_rider_id || bike.rider_id)) {
                              const assignedRiderId = bike.assigned_rider_id || bike.rider_id;
                              const assignedRider = riders.find(r => r.id === assignedRiderId);
                              if (assignedRider) {
                                bikeData.rider_name = assignedRider.name;
                                toast.success(`Auto-filled rider: ${assignedRider.name}`);
                              }
                            }
                            
                            setAccidentForm({...accidentForm, ...bikeData});
                          }}
                          placeholder="Select motorcycle"
                          emptyMessage="No motorcycles found"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rider Name *</Label>
                        <SearchableSelect
                          options={riders.map(rider => ({
                            value: rider.name,
                            label: rider.name,
                            searchText: `${rider.name} ${rider.emirates_id} ${rider.mobile_uae || ''}`,
                            metadata: {
                              'Emirates ID': rider.emirates_id,
                              'Mobile': rider.mobile_uae,
                              'License': rider.license_number
                            }
                          }))}
                          value={accidentForm.rider_name}
                          onValueChange={(value) => setAccidentForm({...accidentForm, rider_name: value})}
                          placeholder="Select rider"
                          emptyMessage="No riders found"
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label>Accident Location *</Label>
                        <Input value={accidentForm.location} onChange={(e) => setAccidentForm({...accidentForm, location: e.target.value})} placeholder="Enter accident location" required />
                      </div>
                      <div className="space-y-2">
                        <Label>Police Report Type *</Label>
                        <Select value={accidentForm.police_report_type} onValueChange={(value) => setAccidentForm({...accidentForm, police_report_type: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="green">Green (Minor)</SelectItem>
                            <SelectItem value="red">Red (Major)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Police Report Documents (Multiple Photos/PDFs)</Label>
                        {accidentForm.police_report_photos.map((photo, index) => (
                          <div key={index} className="flex gap-2 items-center p-2 bg-gray-50 rounded">
                            <FileText className="w-4 h-4" />
                            <a href={photo} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 flex-1 truncate">
                              Police Report {index + 1}
                            </a>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => {
                                const newPhotos = accidentForm.police_report_photos.filter((_, i) => i !== index);
                                setAccidentForm({ ...accidentForm, police_report_photos: newPhotos });
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <FileUpload 
                          label="Upload Police Report"
                          value=""
                          onChange={(url) => setAccidentForm({ 
                            ...accidentForm, 
                            police_report_photos: [...accidentForm.police_report_photos, url] 
                          })}
                          accept="application/pdf,image/*"
                          API={API}
                          getAuthHeader={getAuthHeader}
                        />
                        <p className="text-xs text-slate-500">Upload multiple police report PDFs or photos</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>ODO Reading (KM)</Label>
                        <Input type="number" min="0" value={accidentForm.odometer_km} onChange={(e) => setAccidentForm({...accidentForm, odometer_km: e.target.value})} placeholder="e.g. 12450" data-testid="accident-odometer" />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input value={accidentForm.description} onChange={(e) => setAccidentForm({...accidentForm, description: e.target.value})} placeholder="Describe the accident and damage" />
                      </div>
                    </div>
                    
                    {/* Bike Damage Photos */}
                    <MultiPhotoUpload
                      photos={accidentForm.bike_photos}
                      onChange={(photos) => setAccidentForm({ ...accidentForm, bike_photos: photos })}
                      label="Bike Damage Photos"
                      helperText="Upload multiple photos showing the bike damage"
                      API={API}
                      getAuthHeader={getAuthHeader}
                    />
                    
                    {/* Estimated Damage Cost (Maintenance Estimate) */}
                    <div className="space-y-2">
                      <Label>Maintenance Estimate - Damage Cost (Dh) *</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={accidentForm.estimated_damage_cost} 
                        onChange={(e) => setAccidentForm({...accidentForm, estimated_damage_cost: parseFloat(e.target.value) || 0})} 
                        placeholder="Enter estimated repair cost"
                        required
                      />
                      <p className="text-xs text-slate-500">This estimate will be used for the maintenance record and insurance claim amount (if submitted)</p>
                    </div>
                    
                    {/* Insurance Claim Confirmation */}
                    <div className="border rounded-lg p-4 bg-blue-50">
                      <div className="flex items-start gap-3">
                        <input 
                          type="checkbox"
                          checked={accidentForm.submit_insurance_claim}
                          onChange={(e) => setAccidentForm({...accidentForm, submit_insurance_claim: e.target.checked})}
                          className="w-5 h-5 text-blue-600 rounded mt-0.5"
                        />
                        <div className="flex-1">
                          <Label className="text-base font-semibold cursor-pointer">Submit Insurance Claim for This Accident</Label>
                          <p className="text-xs text-slate-600 mt-1">
                            Check this box to automatically create an insurance claim record. The claim amount will be set from the maintenance estimate above.
                          </p>
                          {accidentForm.submit_insurance_claim && (
                            <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                              <p className="text-xs text-slate-700 space-y-1">
                                <span className="block font-semibold text-blue-700">✓ Insurance Claim will be created with:</span>
                                <span className="block">• Claim Amount: <strong><CurrencyDisplay amount={accidentForm.estimated_damage_cost || 0} /></strong> (from maintenance estimate)</span>
                                <span className="block">• Police Report Type: <strong className="uppercase">{accidentForm.police_report_type}</strong></span>
                                <span className="block">• Status: <strong>Pending</strong></span>
                                <span className="block mt-2 text-slate-500">The claim will be linked to this bike&apos;s active insurance policy and can be updated later.</span>
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> Submitting this report will automatically set the motorcycle status to &quot;Accident&quot; and create a maintenance record with the estimated cost.
                      </p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setAccidentDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white font-semibold rounded-xl shadow-md">Submit Report</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Recovery Vehicle button — navigates to the dedicated sub-page */}
              <Button
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50 text-xs px-2 py-1.5 h-8 whitespace-nowrap"
                size="sm"
                onClick={() => navigate('/motorcycles/recovery')}
                data-testid="recovery-vehicle-nav-btn"
              >
                <Truck className="w-3 h-3 mr-1" />
                {isMobile ? '' : 'Recovery Vehicle'}
              </Button>
            </>
          )}
        
        {/* Combined Bulk Upload Dropdown — gated by motorcycles.create permission
             (previously hard-coded to Admin/Super Admin which hid it from
             authorized non-admin users like Fleet Managers). */}
        {canCreate && (
          <MotorcycleBulkUpload
            downloadTemplate={downloadMotorcycleTemplate}
            onSuccess={fetchData}
            getAuthHeader={getAuthHeader}
            API={API}
            showFixMissingCards={true}
            BulkRegistrationCardComponent={BulkRegistrationCardUpload}
          />
        )}
      </div>
      </div>

      {/* Motorcycle Summary Cards - Interactive */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card 
          className={`border-2 shadow-md bg-gradient-to-br from-blue-50 to-blue-100 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${statusFilter === 'all' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent'}`}
          onClick={() => { setStatusFilter('all'); setCurrentPage(1); fetchData(1, activeSearch, 'all'); }}
          data-testid="summary-total-motorcycles"
        >
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-600">Total Motorcycles</p>
                <p className="text-2xl font-bold text-blue-800">{stats.total}</p>
              </div>
              <div className="p-2 bg-blue-200 rounded-full">
                <Bike className="h-5 w-5 text-blue-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 shadow-md bg-gradient-to-br from-green-50 to-green-100 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${statusFilter === 'leased' ? 'border-green-500 ring-2 ring-green-200' : 'border-transparent'}`}
          onClick={() => { setStatusFilter('leased'); setCurrentPage(1); fetchData(1, activeSearch, 'leased'); }}
          data-testid="summary-leased-motorcycles"
        >
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-green-600">Leased Motorcycles</p>
                <p className="text-2xl font-bold text-green-800">{stats.leased}</p>
              </div>
              <div className="p-2 bg-green-200 rounded-full">
                <CheckCircle className="h-5 w-5 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 shadow-md bg-gradient-to-br from-gray-50 to-gray-100 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${statusFilter === 'available' ? 'border-gray-500 ring-2 ring-gray-300' : 'border-transparent'}`}
          onClick={() => { setStatusFilter('available'); setCurrentPage(1); fetchData(1, activeSearch, 'available'); }}
          data-testid="summary-available-motorcycles"
        >
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600">Available Motorcycles</p>
                <p className="text-2xl font-bold text-gray-800">{stats.available}</p>
              </div>
              <div className="p-2 bg-gray-200 rounded-full">
                <Package className="h-5 w-5 text-gray-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 shadow-md bg-gradient-to-br from-amber-50 to-orange-100 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${statusFilter === 'pending' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-transparent'}`}
          onClick={() => setStatusFilter('pending')}
          data-testid="summary-pending-motorcycles"
        >
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-amber-600">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-800">{stats.pending}</p>
              </div>
              <div className="p-2 bg-amber-200 rounded-full">
                <Clock className="h-5 w-5 text-amber-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lessee-wise Summary (expandable/collapsible, mirrors Fines page pattern) */}
      <Card className="border-0 shadow-lg" data-testid="moto-lessee-summary-card">
        <CardHeader
          className="cursor-pointer"
          onClick={() => setLesseeSummaryExpanded(!lesseeSummaryExpanded)}
          data-testid="moto-lessee-summary-toggle"
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-green-600" />
              Lessee-wise Summary
              {lesseeSummary.length > 0 && (
                <Badge variant="secondary" className="ml-2">{lesseeSummary.length}</Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              {lesseeSummaryExpanded && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs gap-1"
                  onClick={(e) => { e.stopPropagation(); fetchLesseeSummary(); }}
                  data-testid="moto-lessee-summary-refresh"
                >
                  <RefreshCw className={`w-3 h-3 ${lesseeSummaryLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              )}
              <span className={`transition-transform ${lesseeSummaryExpanded ? 'rotate-180' : ''}`}>▼</span>
            </div>
          </div>
        </CardHeader>
        {lesseeSummaryExpanded && (
          <CardContent className="pt-0">
            {lesseeSummaryLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader className="w-5 h-5 animate-spin text-green-600" />
                <span className="ml-2 text-sm text-slate-500">Loading summary...</span>
              </div>
            ) : lesseeSummary.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No lessee data available</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Lessee</TableHead>
                      <TableHead className="text-center">Leased Motorcycles</TableHead>
                      <TableHead className="text-center">Available Motorcycles</TableHead>
                      <TableHead className="text-center">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lesseeSummary.map((row, idx) => {
                      const key = `${row.lessee_id || 'unassigned'}||${row.lessee_name}`;
                      const isExpanded = expandedLesseeKey === key;
                      return (
                        <React.Fragment key={key}>
                          <TableRow
                            className={`cursor-pointer hover:bg-slate-50 text-sm ${row.is_registered === false ? 'bg-amber-50/40' : ''}`}
                            onClick={() => setExpandedLesseeKey(isExpanded ? null : key)}
                            data-testid={`moto-lessee-summary-row-${idx}`}
                          >
                            <TableCell className="text-center text-slate-400">
                              <span className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                            </TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {row.lessee_name}
                                {row.is_registered === false && (
                                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]" title="Bikes in this group reference a lessee name that is NOT in the registered Lessees list. Fix the bike's lessee assignment or add the lessee to clean this up.">
                                    Needs cleanup
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                {row.leased_count}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                                {row.available_count}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center font-semibold">{row.total_count}</TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={5} className="p-0 bg-slate-50">
                                <div className="p-3 max-h-[360px] overflow-y-auto" data-testid={`moto-lessee-summary-detail-${idx}`}>
                                  {row.is_registered === false && Array.isArray(row.unregistered_names) && row.unregistered_names.length > 0 && (
                                    <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                                      <div className="font-medium text-amber-800 mb-1">⚠ Unregistered lessee names found on these bikes:</div>
                                      <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                                        {row.unregistered_names.map((un, i) => (
                                          <li key={i}>
                                            <span className="font-mono">"{un.name}"</span> — {un.count} bike{un.count === 1 ? '' : 's'}
                                          </li>
                                        ))}
                                      </ul>
                                      <div className="mt-1 text-amber-600 italic">
                                        Either add these companies to the Lessees page, or update each motorcycle's lessee assignment to a registered company.
                                      </div>
                                    </div>
                                  )}
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-white">
                                        <TableHead className="text-xs">Plate</TableHead>
                                        <TableHead className="text-xs">Model</TableHead>
                                        <TableHead className="text-xs">Color</TableHead>
                                        <TableHead className="text-xs">Year</TableHead>
                                        <TableHead className="text-xs">Status</TableHead>
                                        <TableHead className="text-xs">Lease</TableHead>
                                        <TableHead className="text-xs">Assigned Rider</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {row.bikes.map((b) => (
                                        <TableRow key={b.id} className="text-xs">
                                          <TableCell className="font-mono">
                                            <PlatePair oldPlate={b.plate_number} goldenPlate={b.golden_plate_number} compact />
                                          </TableCell>
                                          <TableCell>{b.model_name || '-'}{b.model_version ? ` ${b.model_version}` : ''}</TableCell>
                                          <TableCell>{b.color || '-'}</TableCell>
                                          <TableCell>{b.model_year || '-'}</TableCell>
                                          <TableCell>
                                            <Badge variant="outline" className="text-[10px]">{b.status || '-'}</Badge>
                                          </TableCell>
                                          <TableCell>
                                            <Badge
                                              variant="outline"
                                              className={`text-[10px] ${b.lease_status === 'leased' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}
                                            >
                                              {b.lease_status}
                                            </Badge>
                                          </TableCell>
                                          <TableCell>{b.assigned_rider_name || '—'}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                  <div className="mt-2 text-xs text-slate-500">
                                    Showing <b>{row.bikes.length}</b> motorcycle{row.bikes.length === 1 ? '' : 's'} for <b>{row.lessee_name}</b>
                                    {' '}(Leased: {row.leased_count}, Available: {row.available_count})
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      <Tabs defaultValue="motorcycles" className="space-y-4">
        <TabsList className={`${isMobile ? 'flex flex-wrap gap-1 h-auto p-1' : 'grid w-full grid-cols-6'}`}>
          <TabsTrigger value="motorcycles" className={isMobile ? 'text-xs px-2 py-1.5' : ''}>
            {isMobile ? 'Fleet' : 'Motorcycles'}
          </TabsTrigger>
          <TabsTrigger value="handovers" className={isMobile ? 'text-xs px-2 py-1.5' : ''}>
            {isMobile ? 'Handover' : 'Handover Log'}
          </TabsTrigger>
          <TabsTrigger value="takeovers" className={isMobile ? 'text-xs px-2 py-1.5' : ''}>
            {isMobile ? 'Takeover' : 'Takeover Log'}
          </TabsTrigger>
          <TabsTrigger value="accidents" className={isMobile ? 'text-xs px-2 py-1.5' : ''}>
            {isMobile ? 'Accidents' : 'Accident Reports'}
          </TabsTrigger>
          <TabsTrigger value="replacements" className={isMobile ? 'text-xs px-2 py-1.5' : ''}>
            {isMobile ? 'Replace' : 'Replace a Motorcycle'}
          </TabsTrigger>
          <TabsTrigger value="lease-history" data-testid="tab-lease-history" className={isMobile ? 'text-xs px-2 py-1.5' : ''}>
            {isMobile ? 'Lease History' : 'Lease History'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="motorcycles">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Fleet Overview ({filteredMotorcycles.length}{totalCount > filteredMotorcycles.length ? ` of ${totalCount}` : ''})</CardTitle>
                {statusFilter !== 'all' && (
                  <Badge className={`${
                    statusFilter === 'leased' ? 'bg-green-100 text-green-800' : 
                    statusFilter === 'available' ? 'bg-gray-100 text-gray-800' :
                    statusFilter === 'pending' ? 'bg-amber-100 text-amber-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    Showing: {statusFilter === 'pending' ? 'pending approval' : statusFilter} motorcycles
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
          <div className="mb-4 space-y-4">
            {/* Search Bar */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                {isSearching ? (
                  <Loader className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 w-4 h-4 animate-spin" />
                ) : (
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                )}
                <Input
                  type="text"
                  placeholder={t('common.search') + ' (searches all records)'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  className="pl-10"
                  data-testid="motorcycles-search-input"
                />
                {searchQuery && (
                  <button 
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="motorcycles-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button 
                onClick={handleSearch}
                disabled={isSearching}
                className="bg-lime-600 hover:bg-lime-700 text-white px-4"
                data-testid="motorcycles-search-button"
              >
                <Search className="w-4 h-4 mr-1" />
                Search
              </Button>
            </div>

            {/* Advanced Filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Select value={advancedFilters.lessee} onValueChange={(value) => setAdvancedFilters({...advancedFilters, lessee: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Lessee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Lessees</SelectItem>
                  {[...new Set(allMotorcycles.map(m => m.lessee_name).filter(Boolean))].map(lessee => (
                    <SelectItem key={lessee} value={lessee}>{lessee}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={advancedFilters.riderName} onValueChange={(value) => setAdvancedFilters({...advancedFilters, riderName: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Rider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Riders</SelectItem>
                  {[...new Set(allMotorcycles.map(m => m.assigned_rider_name).filter(Boolean))].map(rider => (
                    <SelectItem key={rider} value={rider}>{rider}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={advancedFilters.model} onValueChange={(value) => setAdvancedFilters({...advancedFilters, model: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Model" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Models</SelectItem>
                  {[...new Set(allMotorcycles.map(m => m.model_name).filter(Boolean))].map(model => (
                    <SelectItem key={model} value={model}>{model}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={advancedFilters.city} onValueChange={(value) => setAdvancedFilters({...advancedFilters, city: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by City" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  <SelectItem value="Dubai">Dubai</SelectItem>
                  <SelectItem value="Abu Dhabi">Abu Dhabi</SelectItem>
                  <SelectItem value="Sharjah">Sharjah</SelectItem>
                  <SelectItem value="Ajman">Ajman</SelectItem>
                  <SelectItem value="Umm Al Quwain">Umm Al Quwain</SelectItem>
                  <SelectItem value="Ras Al Khaimah">Ras Al Khaimah</SelectItem>
                  <SelectItem value="Fujairah">Fujairah</SelectItem>
                </SelectContent>
              </Select>

              <Select value={advancedFilters.status} onValueChange={(value) => setAdvancedFilters({...advancedFilters, status: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="in-use">In Use</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="accident">Accident</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>

              <Select value={advancedFilters.leaseStatus} onValueChange={(value) => setAdvancedFilters({...advancedFilters, leaseStatus: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Lease Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Lease Statuses</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="leased">Leased</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchQuery('');
                  setActiveSearch('');
                  setAdvancedFilters({
                    lessee: '',
                    riderName: '',
                    plateNumber: '',
                    model: '',
                    version: '',
                    city: '',
                    status: '',
                    leaseStatus: ''
                  });
                }}
                className="col-span-1"
              >
                Clear Filters
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => setGofleetManagerOpen(true)}
                className="col-span-1 gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <Navigation className="w-4 h-4" />
                GoFleet Devices
              </Button>
            </div>
          </div>
          
          {/* Mobile/Tablet Card View */}
          {(isMobile || isTablet) ? (
            <div className="space-y-3">
              {filteredMotorcycles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Bike className="w-12 h-12 mb-3 text-slate-300" />
                  <p className="text-sm">{activeFilter ? 'No motorcycles match filter' : 'No motorcycles found'}</p>
                </div>
              ) : (
                filteredMotorcycles.map((motorcycle) => (
                  <Card 
                    key={motorcycle.id} 
                    className="overflow-hidden active:scale-[0.99] transition-transform cursor-pointer"
                    onClick={() => navigate(`/motorcycles/${motorcycle.id}/history`)}
                    data-testid={`motorcycle-card-${motorcycle.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Header - Plate & Status */}
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className="bg-purple-100 text-purple-800 font-mono font-semibold text-xs">
                              {motorcycle.internal_id || 'N/A'}
                            </Badge>
                            <PlatePair
                              oldPlate={motorcycle.plate_number}
                              goldenPlate={motorcycle.golden_plate_number}
                            />
                          </div>

                          {/* Status Badges */}
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            <Badge className={`text-xs ${getStatusColor(motorcycle.status)}`}>
                              {motorcycle.status}
                            </Badge>
                            <Badge className={`text-xs ${motorcycle.lease_status === 'leased' ? 'bg-blue-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {motorcycle.lease_status}
                            </Badge>
                            {renderGpsStatusBadge(motorcycle)}
                          </div>

                          {/* Details Grid */}
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            <div>
                              <span className="text-slate-500 text-xs">Model:</span>
                              <p className="text-slate-700 truncate">{motorcycle.model_name || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Year:</span>
                              <p className="text-slate-700">{motorcycle.year_of_manufacturing || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Company:</span>
                              <p className="text-slate-700 truncate">{motorcycle.lessee_name || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Reg. Exp:</span>
                              <p className="text-slate-700">{motorcycle.registration_expiry_date ? formatDateDisplay(motorcycle.registration_expiry_date) : '-'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => { setTrackingMotorcycle(motorcycle); setTrackingDialogOpen(true); }}>
                                <Navigation className="mr-2 h-4 w-4 text-blue-600" />
                                Track Bike
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/motorcycles/${motorcycle.id}/history`)}>
                                <History className="mr-2 h-4 w-4" />
                                View History
                              </DropdownMenuItem>
                              {motorcycle.registration_card_url && (
                                <DropdownMenuItem onClick={() => window.open(motorcycle.registration_card_url, '_blank')}>
                                  <Download className="mr-2 h-4 w-4" />
                                  Download Reg. Card
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => {
                                  if (motorcycle.food_permit_url) {
                                    window.open(motorcycle.food_permit_url, '_blank');
                                  } else {
                                    toast.info('Food Permit for this Motorcycle is not available');
                                  }
                                }}
                                data-testid={`download-food-permit-mobile-${motorcycle.id}`}
                              >
                                <Download className="mr-2 h-4 w-4" />
                                Download Food Permit
                              </DropdownMenuItem>
                              {user?.role !== 'Account Manager' && (
                                <DropdownMenuItem onClick={() => handleEdit(motorcycle)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              {permissions.canDelete && (
                                <DropdownMenuItem onClick={() => handleDelete(motorcycle.id)} className="text-red-600">
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          ) : (
            /* Desktop Table View */
            <div className="rounded-lg border border-slate-200">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold whitespace-nowrap px-1.5 py-2">ID</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2">Plate (B&amp;W)</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2">Golden</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2">Chassis</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2">Brand</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2">Model</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2">Color</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2">Year</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2">Reg. Exp</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2">{t('motorcycles.status')}</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2">Lease</TableHead>
                  <TableHead className="whitespace-nowrap px-1.5 py-2 max-w-[110px]">Lessee</TableHead>
                  <TableHead className="whitespace-nowrap px-1 py-2 w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMotorcycles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center text-slate-500 py-8">
                      {activeFilter ? 'No motorcycles match the current filter.' : 'No motorcycles found. Add your first motorcycle to get started.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMotorcycles.map((motorcycle) => (
                    <TableRow 
                      key={motorcycle.id} 
                      data-testid={`motorcycle-row-${motorcycle.id}`}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/motorcycles/${motorcycle.id}/history`)}
                    >
                      <TableCell className="whitespace-nowrap px-1.5 py-1.5">
                        <Badge className="bg-purple-100 text-purple-800 font-mono font-semibold text-xs px-1.5 py-0.5">
                          {motorcycle.internal_id || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-1.5 py-1.5">
                        <span className="inline-flex items-center font-mono font-semibold rounded-sm border text-[11px] px-1.5 py-0.5 whitespace-nowrap bg-white text-black border-black">
                          {motorcycle.plate_number}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-1.5 py-1.5">
                        {motorcycle.golden_plate_number ? (
                          <span className="inline-flex items-center font-mono font-semibold rounded-sm border text-[11px] px-1.5 py-0.5 whitespace-nowrap bg-yellow-300 text-black border-yellow-600" title="Golden plate">
                            {motorcycle.golden_plate_number}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap px-1.5 py-1.5 max-w-[85px] truncate" title={motorcycle.chassis_number}>{motorcycle.chassis_number}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap px-1.5 py-1.5 max-w-[80px] truncate" title={motorcycle.brand}>{motorcycle.brand || '-'}</TableCell>
                      <TableCell className="whitespace-nowrap px-1.5 py-1.5 text-xs max-w-[70px] truncate" title={motorcycle.model_name}>{motorcycle.model_name}</TableCell>
                      <TableCell className="whitespace-nowrap px-1.5 py-1.5 text-xs max-w-[60px] truncate" title={motorcycle.color}>{motorcycle.color}</TableCell>
                      <TableCell className="whitespace-nowrap px-1.5 py-1.5 text-xs">{motorcycle.year_of_manufacturing}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap px-1.5 py-1.5">{motorcycle.registration_expiry_date ? formatDateDisplay(motorcycle.registration_expiry_date) : '-'}</TableCell>
                      <TableCell className="px-1.5 py-1.5">
                        <Badge className={`text-[10px] px-1.5 py-0.5 ${getStatusColor(motorcycle.status)}`}>
                          {motorcycle.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-1.5 py-1.5">
                        <Badge className={`text-[10px] px-1.5 py-0.5 ${motorcycle.lease_status === 'leased' ? 'bg-blue-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {motorcycle.lease_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-1.5 py-1.5 text-xs max-w-[110px] truncate" title={motorcycle.lessee_name}>{motorcycle.lessee_name || '-'}</TableCell>
                      <TableCell className="px-1 py-1.5">
                        <div onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                data-testid={`actions-motorcycle-${motorcycle.id}`}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => {
                                  setTrackingMotorcycle(motorcycle);
                                  setTrackingDialogOpen(true);
                                }} 
                                className="cursor-pointer"
                              >
                                <Navigation className="mr-2 h-4 w-4 text-blue-600" />
                                Track Bike
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => navigate(`/motorcycles/${motorcycle.id}/history`)} 
                                className="cursor-pointer"
                              >
                                <History className="mr-2 h-4 w-4" />
                                View History
                              </DropdownMenuItem>
                              {motorcycle.registration_card_url && (
                                <DropdownMenuItem 
                                  onClick={() => window.open(motorcycle.registration_card_url, '_blank')} 
                                  className="cursor-pointer"
                                >
                                  <Download className="mr-2 h-4 w-4" />
                                  Download Reg. Card
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => {
                                  if (motorcycle.food_permit_url) {
                                    window.open(motorcycle.food_permit_url, '_blank');
                                  } else {
                                    toast.info('Food Permit for this Motorcycle is not available');
                                  }
                                }}
                                className="cursor-pointer"
                                data-testid={`download-food-permit-${motorcycle.id}`}
                              >
                                <Download className="mr-2 h-4 w-4" />
                                Download Food Permit
                              </DropdownMenuItem>
                              {user?.role !== 'Account Manager' && (
                                <DropdownMenuItem 
                                  onClick={() => handleEdit(motorcycle)} 
                                  className="cursor-pointer"
                                  data-testid={`edit-motorcycle-${motorcycle.id}`}
                                >
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              {permissions.canDelete && (
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(motorcycle.id)} 
                                  className="cursor-pointer text-red-600 focus:text-red-600"
                                  data-testid={`delete-motorcycle-${motorcycle.id}`}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
              totalCount={totalCount}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>

    {/* Handover Log Tab */}
    <TabsContent value="handovers">
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle>Handover Log</CardTitle>
          <CardDescription>All motorcycle handover records</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search handovers (plate, lessee, rider, technician)..."
                value={handoverSearch}
                onChange={(e) => setHandoverSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Handovers Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Plate Number</TableHead>
                    <TableHead>Lessee</TableHead>
                    <TableHead>Rider Name</TableHead>
                    <TableHead>Technician</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>ODO (KM)</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHandovers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No handover records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredHandovers.map((handover) => (
                      <TableRow key={handover.id}>
                        <TableCell className="font-medium">
                          {handover.handover_date}<br/>
                          <span className="text-xs text-slate-500">{handover.handover_time}</span>
                        </TableCell>
                        <TableCell><PlatePair oldPlate={handover.bike_plate_number || handover.bike_plate || handover.motorcycle_plate} goldenPlate={handover.bike_golden_plate_number} compact /></TableCell>
                        <TableCell>{handover.lessee_name}</TableCell>
                        <TableCell>{handover.rider_name}</TableCell>
                        <TableCell>{handover.technician_name}</TableCell>
                        <TableCell>{handover.location || handover.location_name}</TableCell>
                        <TableCell className="font-mono text-sm">{handover.odometer_km ? `${Number(handover.odometer_km).toLocaleString()} km` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`handover-actions-${handover.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                data-testid={`handover-download-${handover.id}`}
                                onClick={async () => {
                                  try {
                                    const res = await axios.get(
                                      `${API}/motorcycles/handovers/${handover.id}/certificate.pdf`,
                                      { headers: getAuthHeader(), responseType: 'blob' },
                                    );
                                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `Handover_${handover.bike_plate_number || 'certificate'}.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    URL.revokeObjectURL(url);
                                    toast.success('PDF downloaded successfully');
                                  } catch (error) {
                                    toast.error('Failed to generate PDF');
                                    console.error('PDF generation error:', error);
                                  }
                                }}
                              >
                                <FileDown className="w-4 h-4 mr-2" /> Download PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`handover-regen-${handover.id}`}
                                onClick={async () => {
                                  const toastId = toast.loading('Regenerating certificate with latest template…');
                                  try {
                                    const res = await axios.get(
                                      `${API}/motorcycles/handovers/${handover.id}/certificate.pdf?regen=${Date.now()}`,
                                      { headers: { ...getAuthHeader(), 'Cache-Control': 'no-cache' }, responseType: 'blob' },
                                    );
                                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `Handover_${handover.bike_plate_number || 'certificate'}_regenerated.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    URL.revokeObjectURL(url);
                                    toast.success('Certificate regenerated', { id: toastId, description: 'Includes latest signature/thumbprint/EID/DL rendering fix.' });
                                  } catch (error) {
                                    toast.error('Regeneration failed', { id: toastId });
                                    console.error('regen error:', error);
                                  }
                                }}
                              >
                                <RefreshCw className="w-4 h-4 mr-2" /> Regenerate Certificate
                              </DropdownMenuItem>
                              {canLog('handover', 'edit') && (
                                  <DropdownMenuItem
                                    data-testid={`handover-edit-${handover.id}`}
                                    onClick={() => openLogEdit('handover', handover)}
                                  >
                                    <Edit className="w-4 h-4 mr-2" /> Edit
                                  </DropdownMenuItem>
                              )}
                              {canLog('handover', 'delete') && (
                                  <DropdownMenuItem
                                    data-testid={`handover-delete-${handover.id}`}
                                    className="text-red-600 focus:text-red-700"
                                    onClick={() => openLogDelete('handover', handover)}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </TabsContent>

    {/* Takeover Log Tab */}
    <TabsContent value="takeovers">
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle>Takeover Log</CardTitle>
          <CardDescription>All motorcycle takeover records</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search takeovers (plate, lessee, rider, technician)..."
                value={takeoverSearch}
                onChange={(e) => setTakeoverSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Takeovers Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Plate Number</TableHead>
                    <TableHead>Lessee</TableHead>
                    <TableHead>Rider Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Technician</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>ODO (KM)</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTakeovers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        No takeover records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTakeovers.map((takeover) => (
                      <TableRow key={takeover.id}>
                        <TableCell className="font-medium">
                          {takeover.takeover_date}<br/>
                          <span className="text-xs text-slate-500">{takeover.takeover_time}</span>
                        </TableCell>
                        <TableCell><PlatePair oldPlate={takeover.bike_plate_number || takeover.bike_plate || takeover.motorcycle_plate} goldenPlate={takeover.bike_golden_plate_number} compact /></TableCell>
                        <TableCell>{takeover.lessee_name}</TableCell>
                        <TableCell>{takeover.rider_name}</TableCell>
                        <TableCell>
                          <Badge className={
                            takeover.takeover_type === 'permanent' ? 'bg-red-100 text-red-800' :
                            takeover.takeover_type === 'temporary' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }>
                            {takeover.takeover_type}
                          </Badge>
                        </TableCell>
                        <TableCell>{takeover.technician_name}</TableCell>
                        <TableCell>{takeover.location || takeover.location_name}</TableCell>
                        <TableCell className="font-mono text-sm">{takeover.odometer_km ? `${Number(takeover.odometer_km).toLocaleString()} km` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`takeover-actions-${takeover.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                data-testid={`takeover-download-${takeover.id}`}
                                onClick={async () => {
                                  try {
                                    const res = await axios.get(
                                      `${API}/motorcycles/takeovers/${takeover.id}/certificate.pdf`,
                                      { headers: getAuthHeader(), responseType: 'blob' },
                                    );
                                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `Takeover_${takeover.bike_plate_number || 'certificate'}.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    URL.revokeObjectURL(url);
                                    toast.success('PDF downloaded successfully');
                                  } catch (error) {
                                    toast.error('Failed to generate PDF');
                                    console.error('PDF generation error:', error);
                                  }
                                }}
                              >
                                <FileDown className="w-4 h-4 mr-2" /> Download PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`takeover-regen-${takeover.id}`}
                                onClick={async () => {
                                  const toastId = toast.loading('Regenerating certificate with latest template…');
                                  try {
                                    const res = await axios.get(
                                      `${API}/motorcycles/takeovers/${takeover.id}/certificate.pdf?regen=${Date.now()}`,
                                      { headers: { ...getAuthHeader(), 'Cache-Control': 'no-cache' }, responseType: 'blob' },
                                    );
                                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `Takeover_${takeover.bike_plate_number || 'certificate'}_regenerated.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    URL.revokeObjectURL(url);
                                    toast.success('Certificate regenerated', { id: toastId, description: 'Includes latest signature/thumbprint/EID/DL rendering fix.' });
                                  } catch (error) {
                                    toast.error('Regeneration failed', { id: toastId });
                                    console.error('regen error:', error);
                                  }
                                }}
                              >
                                <RefreshCw className="w-4 h-4 mr-2" /> Regenerate Certificate
                              </DropdownMenuItem>
                              {canLog('takeover', 'edit') && (
                                  <DropdownMenuItem
                                    data-testid={`takeover-edit-${takeover.id}`}
                                    onClick={() => openLogEdit('takeover', takeover)}
                                  >
                                    <Edit className="w-4 h-4 mr-2" /> Edit
                                  </DropdownMenuItem>
                              )}
                              {canLog('takeover', 'delete') && (
                                  <DropdownMenuItem
                                    data-testid={`takeover-delete-${takeover.id}`}
                                    className="text-red-600 focus:text-red-700"
                                    onClick={() => openLogDelete('takeover', takeover)}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </TabsContent>

    {/* Accident Reports Tab */}
    <TabsContent value="accidents">
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle>Accident Reports</CardTitle>
          <CardDescription>All accident reports and incidents</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search accidents (plate, rider, location, report type)..."
                value={accidentSearch}
                onChange={(e) => setAccidentSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Accidents Table/Cards */}
            {isMobile ? (
              /* Mobile Card View for Accidents */
              <div className="divide-y divide-slate-100">
                {filteredAccidents.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">No accident reports found</div>
                ) : (
                  filteredAccidents.map((accident) => (
                    <div key={accident.id} className="p-4 hover:bg-blue-50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-medium"><PlatePair oldPlate={accident.bike_plate_number || accident.bike_plate || accident.motorcycle_plate} goldenPlate={accident.bike_golden_plate_number} compact /></div>
                          <div className="text-xs text-slate-500">{formatDateDisplay(accident.accident_date)} {accident.accident_time}</div>
                        </div>
                        <Badge className={
                          accident.police_report_type === 'green' ? 'bg-green-100 text-green-800' :
                          accident.police_report_type === 'white' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {accident.police_report_type?.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-2">
                        <div><span className="text-slate-500">Rider:</span> {accident.rider_name}</div>
                        <div><span className="text-slate-500">Location:</span> {accident.location || accident.location_name}</div>
                        <div className="col-span-2"><span className="text-slate-500">Company:</span> {accident.lessee_name || 'N/A'}</div>
                      </div>
                      <div className="text-xs text-slate-600 mb-2 truncate">
                        <span className="text-slate-500">Damages:</span> {accident.damages_description || 'N/A'}
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                        <Badge className="bg-orange-100 text-orange-800">Under Repair</Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              const motorcycle = allMotorcycles.find(m => m.id === accident.bike_id);
                              await generateAccidentPDF(accident, motorcycle);
                              toast.success('PDF downloaded');
                            } catch (error) {
                              toast.error('Failed to generate PDF');
                            }
                          }}
                        >
                          <FileDown className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              /* Desktop Table View */
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Plate Number</TableHead>
                      <TableHead>Lessee Company</TableHead>
                      <TableHead>Rider Name</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Police Report</TableHead>
                      <TableHead>Damages</TableHead>
                      <TableHead>ODO (KM)</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAccidents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                          No accident reports found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAccidents.map((accident) => (
                        <TableRow key={accident.id}>
                          <TableCell className="font-medium">
                            {accident.accident_date}<br/>
                            <span className="text-xs text-slate-500">{accident.accident_time}</span>
                          </TableCell>
                          <TableCell><PlatePair oldPlate={accident.bike_plate_number || accident.bike_plate || accident.motorcycle_plate} goldenPlate={accident.bike_golden_plate_number} compact /></TableCell>
                          <TableCell>
                            {accident.lessee_name ? (
                              <span className="font-medium text-slate-700">{accident.lessee_name}</span>
                            ) : (
                              <span className="text-slate-400 text-sm">No Lessee</span>
                            )}
                          </TableCell>
                          <TableCell>{accident.rider_name}</TableCell>
                          <TableCell>{accident.location || accident.location_name}</TableCell>
                          <TableCell>
                            <Badge className={
                              accident.police_report_type === 'green' ? 'bg-green-100 text-green-800' :
                              accident.police_report_type === 'white' ? 'bg-gray-100 text-gray-800' :
                              'bg-red-100 text-red-800'
                            }>
                              {accident.police_report_type.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{accident.damages_description}</TableCell>
                          <TableCell className="font-mono text-sm">{accident.odometer_km ? `${Number(accident.odometer_km).toLocaleString()} km` : <span className="text-slate-300">—</span>}</TableCell>
                          <TableCell>
                            <Badge className="bg-orange-100 text-orange-800">
                              Under Repair
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                try {
                                  const motorcycle = allMotorcycles.find(m => m.id === accident.bike_id);
                                  await generateAccidentPDF(accident, motorcycle);
                                  toast.success('PDF downloaded successfully');
                                } catch (error) {
                                  toast.error('Failed to generate PDF');
                                  console.error('PDF generation error:', error);
                                }
                              }}
                              title="Download PDF"
                            >
                              <FileDown className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </TabsContent>

    {/* Replacement Motorcycle Tab */}
    <TabsContent value="replacements">
      <ReplacementMotorcycle />
    </TabsContent>

    {/* Lease History Tab */}
    <TabsContent value="lease-history">
      <LeaseHistoryReport />
    </TabsContent>
  </Tabs>

      {/* Bike Tracking Dialog */}
      <BikeTrackingDialog
        open={trackingDialogOpen}
        onOpenChange={setTrackingDialogOpen}
        motorcycle={trackingMotorcycle}
        getAuthHeader={getAuthHeader}
      />

      {/* GoFleet Device Manager Dialog */}
      <GoFleetDeviceManager
        open={gofleetManagerOpen}
        onOpenChange={setGofleetManagerOpen}
        getAuthHeader={getAuthHeader}
        onRefresh={() => {
          // Refresh motorcycles to get updated gofleet_device_id
          fetchData();
        }}
      />

      {/* Admin-only: Edit Handover / Takeover Log dialog */}
      <Dialog open={logEditOpen} onOpenChange={(o) => { if (!logSubmitting) setLogEditOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="log-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              Edit {logTarget?.kind === 'handover' ? 'Handover' : 'Takeover'} Record
            </DialogTitle>
            <DialogDescription>
              Plate {logTarget?.record?.bike_plate_number || logTarget?.record?.bike_plate || ''} · {logTarget?.record?.[logTarget?.kind === 'handover' ? 'handover_date' : 'takeover_date'] || ''}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={logEditForm[logTarget?.kind === 'handover' ? 'handover_date' : 'takeover_date'] || ''}
                onChange={(e) => setLogEditForm({ ...logEditForm, [logTarget?.kind === 'handover' ? 'handover_date' : 'takeover_date']: e.target.value })}
                data-testid="log-edit-date"
              />
            </div>
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={logEditForm[logTarget?.kind === 'handover' ? 'handover_time' : 'takeover_time'] || ''}
                onChange={(e) => setLogEditForm({ ...logEditForm, [logTarget?.kind === 'handover' ? 'handover_time' : 'takeover_time']: e.target.value })}
                data-testid="log-edit-time"
              />
            </div>
            <div>
              <Label>Lessee Name</Label>
              <Input
                value={logEditForm.lessee_name || ''}
                onChange={(e) => setLogEditForm({ ...logEditForm, lessee_name: e.target.value })}
                data-testid="log-edit-lessee-name"
              />
            </div>
            <div>
              <Label>Rider Name</Label>
              <Input
                value={logEditForm.rider_name || ''}
                onChange={(e) => setLogEditForm({ ...logEditForm, rider_name: e.target.value })}
                data-testid="log-edit-rider-name"
              />
            </div>
            <div>
              <Label>Rider Emirates ID</Label>
              <Input
                value={logEditForm.rider_emirates_id || ''}
                onChange={(e) => setLogEditForm({ ...logEditForm, rider_emirates_id: e.target.value })}
                data-testid="log-edit-rider-eid"
              />
            </div>
            <div>
              <Label>Technician</Label>
              <Input
                value={logEditForm.technician_name || ''}
                onChange={(e) => setLogEditForm({ ...logEditForm, technician_name: e.target.value })}
                data-testid="log-edit-technician"
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={logEditForm.location || ''}
                onChange={(e) => setLogEditForm({ ...logEditForm, location: e.target.value })}
                data-testid="log-edit-location"
              />
            </div>
            <div>
              <Label>ODO (KM)</Label>
              <Input
                type="number"
                value={logEditForm.odometer_km ?? ''}
                onChange={(e) => setLogEditForm({ ...logEditForm, odometer_km: e.target.value })}
                data-testid="log-edit-odometer"
              />
            </div>
            {logTarget?.kind === 'takeover' && (
              <>
                <div>
                  <Label>Takeover Type</Label>
                  <Select
                    value={logEditForm.takeover_type || 'temporary'}
                    onValueChange={(v) => setLogEditForm({ ...logEditForm, takeover_type: v })}
                  >
                    <SelectTrigger data-testid="log-edit-takeover-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="temporary">Temporary</SelectItem>
                      <SelectItem value="permanent">Permanent</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {logEditForm.takeover_type === 'temporary' && (
                  <>
                    <div>
                      <Label>Temporary Start</Label>
                      <Input
                        type="date"
                        value={logEditForm.temporary_start_date || ''}
                        onChange={(e) => setLogEditForm({ ...logEditForm, temporary_start_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Temporary End</Label>
                      <Input
                        type="date"
                        value={logEditForm.temporary_end_date || ''}
                        onChange={(e) => setLogEditForm({ ...logEditForm, temporary_end_date: e.target.value })}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Temporary Reason</Label>
                      <Input
                        value={logEditForm.temporary_reason || ''}
                        onChange={(e) => setLogEditForm({ ...logEditForm, temporary_reason: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </>
            )}
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Input
                value={logEditForm.notes || ''}
                onChange={(e) => setLogEditForm({ ...logEditForm, notes: e.target.value })}
                data-testid="log-edit-notes"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setLogEditOpen(false)} disabled={logSubmitting}>Cancel</Button>
            <Button onClick={submitLogEdit} disabled={logSubmitting} data-testid="log-edit-save">
              {logSubmitting ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin-only: Delete Handover / Takeover — captures the bike's CURRENT state */}
      <Dialog open={logDeleteOpen} onOpenChange={(o) => { if (!logSubmitting) setLogDeleteOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="log-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Delete {logTarget?.kind === 'handover' ? 'Handover' : 'Takeover'} Record
            </DialogTitle>
            <DialogDescription>
              This record will be removed. Confirm the bike's current status so the motorcycle and rider assignments stay accurate.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800 mb-2">
            Deleting: <strong>{logTarget?.record?.bike_plate_number || logTarget?.record?.bike_plate}</strong>
            {' · '}{logTarget?.record?.rider_name || '—'} · {logTarget?.record?.[logTarget?.kind === 'handover' ? 'handover_date' : 'takeover_date']}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div>
              <Label>Current Bike Status</Label>
              <Select
                value={logDeleteForm.status || 'available'}
                onValueChange={(v) => setLogDeleteForm({ ...logDeleteForm, status: v })}
              >
                <SelectTrigger data-testid="log-delete-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="in_use">In Use</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Current Lease Status</Label>
              <Select
                value={logDeleteForm.lease_status || 'available'}
                onValueChange={(v) => setLogDeleteForm({ ...logDeleteForm, lease_status: v })}
              >
                <SelectTrigger data-testid="log-delete-lease-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="leased">Leased</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Current Lessee</Label>
              <Input
                value={logDeleteForm.lessee_name || ''}
                onChange={(e) => setLogDeleteForm({ ...logDeleteForm, lessee_name: e.target.value })}
                placeholder="Leave blank if not leased"
                data-testid="log-delete-lessee-name"
              />
            </div>
            <div>
              <Label>Current Rider</Label>
              <Input
                value={logDeleteForm.rider_name || ''}
                onChange={(e) => setLogDeleteForm({ ...logDeleteForm, rider_name: e.target.value })}
                placeholder="Leave blank if unassigned"
                data-testid="log-delete-rider-name"
              />
            </div>
            <div>
              <Label>Current Rider ID</Label>
              <Input
                value={logDeleteForm.rider_id || ''}
                onChange={(e) => setLogDeleteForm({ ...logDeleteForm, rider_id: e.target.value })}
                placeholder="Rider UUID (optional)"
              />
            </div>
            <div>
              <Label>Current ODO (KM)</Label>
              <Input
                type="number"
                value={logDeleteForm.odometer_km ?? ''}
                onChange={(e) => setLogDeleteForm({ ...logDeleteForm, odometer_km: e.target.value })}
                data-testid="log-delete-odometer"
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={logDeleteForm.location || ''}
                onChange={(e) => setLogDeleteForm({ ...logDeleteForm, location: e.target.value })}
              />
            </div>
            <div>
              <Label>Technician</Label>
              <Input
                value={logDeleteForm.technician_name || ''}
                onChange={(e) => setLogDeleteForm({ ...logDeleteForm, technician_name: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Reason for deletion (audit trail)</Label>
              <Input
                value={logDeleteForm.notes || ''}
                onChange={(e) => setLogDeleteForm({ ...logDeleteForm, notes: e.target.value })}
                placeholder="e.g., duplicate entry, wrong bike selected, corrected via new handover"
                data-testid="log-delete-notes"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setLogDeleteOpen(false)} disabled={logSubmitting}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={submitLogDelete}
              disabled={logSubmitting}
              data-testid="log-delete-confirm"
            >
              {logSubmitting ? 'Deleting…' : 'Delete & Update Bike'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Motorcycles;